// Billet Pro Shop - Holley EFI .DL binary log parser + six-check analyzer (V6 format)
// Reverse-engineered and validated to float32 precision against 5 real .dl/.csv pairs.

const ROW_BYTES = 4120;
const ROW_FLOATS = ROW_BYTES / 4; // 1030
const ROW0_ELEM = 3090; // validated constant across every test file
const MAGIC_V6 = 0x0085f41f;

// [column index within row (float32 units), row shift]
const CHANNELS = {
  rpm: [1028, 0],
  target_afr: [6, 1],
  afr_left: [8, 1],
  afr_right: [10, 1],
  afr_avg: [12, 1],
  map: [58, 1],
  tps: [60, 1],
  mat: [62, 1],
  cts: [64, 1],
  baro: [66, 1],
  battery: [68, 1],
  oil_pressure: [70, 1],
  fuel_pressure: [72, 1],
  boost_psig: [318, 1],
  boost_target: [342, 1],
  duty_cycle: [2, 1],
  trans_brake: [344, 1],
  boost_time: [340, 1],
  cl_comp: [4, 1],
  fuel_flow: [30, 1],
};

function parseDL(arrayBuffer) {
  const dv = new DataView(arrayBuffer);
  if (arrayBuffer.byteLength < 32) throw new Error("File too small to be a Holley .dl log.");
  const magic = dv.getUint32(0, true);
  const version = Number(dv.getBigUint64(8, true));
  if (magic !== MAGIC_V6 || version !== 6) {
    const err = new Error("UNSUPPORTED_DL_FORMAT");
    err.code = "UNSUPPORTED_DL_FORMAT";
    throw err;
  }

  const totalFloats = Math.floor(arrayBuffer.byteLength / 4);
  const nRows = Math.floor((totalFloats - ROW0_ELEM) / ROW_FLOATS);
  if (nRows < 5) throw new Error("Log too short to analyze.");

  const out = {};
  for (const name of Object.keys(CHANNELS)) out[name] = new Float64Array(nRows).fill(NaN);

  for (let r = 0; r < nRows; r++) {
    for (const [name, [col, shift]] of Object.entries(CHANNELS)) {
      const elemIdx = ROW0_ELEM + (r + shift) * ROW_FLOATS + col;
      const byteOff = elemIdx * 4;
      if (byteOff >= 0 && byteOff + 4 <= arrayBuffer.byteLength) {
        out[name][r] = dv.getFloat32(byteOff, true);
      }
    }
  }
  return { channels: out, nRows };
}

// --- CSV fallback (for logs that aren't the V6 binary format) ---
const CSV_NAME_MAP = {
  rpm: "RPM",
  target_afr: "Target AFR",
  afr_left: "AFR Left",
  afr_right: "AFR Right",
  afr_avg: "AFR Average",
  map: "MAP",
  tps: "TPS",
  mat: "MAT",
  cts: "CTS",
  baro: "Baro",
  battery: "Battery",
  oil_pressure: "Oil Pressure",
  fuel_pressure: "Fuel Pressure",
  boost_psig: "Boost PSIG",
  boost_target: "Target Dome",
  boost_target_direct: "Target Boost",
  rtc: "RTC",
  duty_cycle: "Duty Cycle",
  trans_brake: "Trans Brake",
  boost_time: "Boost Time",
  cl_comp: "CL Comp",
  current_learn: "Current Learn",
  fuel_flow: "Fuel Flow",
};

// Fallback header names for channels where Holley's export naming varies.
// Exact CSV_NAME_MAP match is always tried first; these are only consulted
// if that fails. Matching is done after normalizing case/spacing/punctuation.
const CSV_ALIASES = {
  cl_comp: ["CL Comp", "CL Compensation", "Closed Loop Comp", "Closed Loop Compensation",
    "Closed Loop Correction", "Closed Loop Fuel Correction", "CL Correction", "CL Fuel Comp"],
  current_learn: ["Current Learn", "Learn Comp", "Fuel Learn"],
};

function normalizeHeader(s) {
  return s.toLowerCase().trim()
    .replace(/[_-]+/g, " ")
    .replace(/%/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length < 3) throw new Error("CSV has no data rows.");
  const headers = lines[0].split(",").map((h) => h.trim());
  const normHeaders = headers.map(normalizeHeader);
  // lines[1] is a units row like "[RPM]" - skip it
  const dataLines = lines.slice(2);
  const colIndex = {};
  for (const [key, csvName] of Object.entries(CSV_NAME_MAP)) {
    let idx = headers.indexOf(csvName);
    if (idx === -1 && CSV_ALIASES[key]) {
      for (const alias of CSV_ALIASES[key]) {
        idx = normHeaders.indexOf(normalizeHeader(alias));
        if (idx !== -1) break;
      }
    }
    if (idx !== -1) colIndex[key] = idx;
  }
  const nRows = dataLines.length;
  const out = {};
  for (const name of Object.keys(CSV_NAME_MAP)) out[name] = new Float64Array(nRows).fill(NaN);
  for (let r = 0; r < nRows; r++) {
    const cells = dataLines[r].split(",");
    for (const [key, idx] of Object.entries(colIndex)) {
      const v = parseFloat(cells[idx]);
      if (!Number.isNaN(v)) out[key][r] = v;
    }
  }
  return { channels: out, nRows };
}

// --- six-check analyzer (ports analyze_log.py exactly) ---
function nanmax(a){let m=-Infinity;for(const v of a)if(!Number.isNaN(v)&&v>m)m=v;return m;}
function nanmin(a){let m=Infinity;for(const v of a)if(!Number.isNaN(v)&&v<m)m=v;return m;}
function nanmean(a){let s=0,n=0;for(const v of a)if(!Number.isNaN(v)){s+=v;n++;}return n?s/n:NaN;}
function nanstd(a){const m=nanmean(a);let s=0,n=0;for(const v of a)if(!Number.isNaN(v)){s+=(v-m)**2;n++;}return n?Math.sqrt(s/n):NaN;}
function nanmedian(a){const c=a.filter(v=>!Number.isNaN(v)).sort((x,y)=>x-y);if(!c.length)return NaN;const m=Math.floor(c.length/2);return c.length%2?c[m]:(c[m-1]+c[m])/2;}

// Channel existence should be judged by whether there's real, plausible data,
// not by variance - a genuine constant target (e.g. a fixed 20 psi target) has
// zero standard deviation and is still a perfectly real channel. "missing"
// means mostly NaN (column not present); "unused" means it's populated but
// reads as a default that's implausible for what it's supposed to measure
// (e.g. a boost target channel that sits at ~0 psi the whole log because this
// tune doesn't use that control mode).
function channelStatus(arr, opts = {}) {
  const { minFinitePct = 0.5, plausibleMax = null } = opts;
  let finite = 0;
  for (const v of arr) if (!Number.isNaN(v)) finite++;
  const finitePct = arr.length ? finite / arr.length : 0;
  if (finitePct < minFinitePct) return "missing";
  if (plausibleMax !== null && nanmax(arr) <= plausibleMax) return "unused";
  return "available";
}

// Target Dome is a commanded dome pressure, not necessarily the same physical
// quantity as manifold boost target - on some cars they track closely, on
// others dome pressure tops out far below actual boost (a staged or multi-mode
// boost controller, for example). Only trust it as a boost-target proxy if its
// peak is at least in the same ballpark as the boost actually achieved.
function domeLooksLikeBoostProxy(domeArr, boostArr) {
  const domeMax = nanmax(domeArr), boostMax = nanmax(boostArr);
  if (!(domeMax > 1) || !(boostMax > 1)) return false;
  return domeMax >= 0.4 * boostMax;
}

// Converts a duration in seconds to a sample count at the log's detected rate,
// so detection windows track real time instead of assuming a fixed 20Hz.
function secondsToSamples(seconds, rateHz) {
  return Math.max(1, Math.round(seconds * (rateHz || ASSUMED_RATE_HZ)));
}

// Holley's .dl binary doesn't expose the configured logging rate directly (only
// the CSV export's RTC column does). ASSUMED_RATE_HZ is a reasonable default for
// padding the WOT window in time; when a CSV is uploaded, the real RTC column is
// used instead so the padding is exact rather than estimated.
const ASSUMED_RATE_HZ = 20;

// Tunable thresholds. The UI persists overrides to localStorage; anything not
// overridden falls back to these defaults. Passed through analyze() so a
// customer's settings actually change what gets flagged, not just the labels.
const DEFAULT_SETTINGS = {
  oilPressureRule: "ratio",      // "ratio" (psi per 1000 rpm), "flat" (fixed minimum), "off"
  oilPressureRatio: 10,          // psi per 1000 rpm, used when rule = "ratio"
  oilPressureFlatMin: 20,        // psi, used when rule = "flat"
  boostToleranceP: 3,            // psi, how far off target counts as "off"
  afrLeanTolerance: 0.3,         // AFR points over target counts as "lean"
  coolantTempLimit: 230,         // °F
  intakeTempLimit: 200,          // °F
  dutyCycleWarn: 90,             // %
  dutyCycleCritical: 95,         // %
  clCompMonitor: 5,              // % correction, general diagnostic default, not a universal rule
  clCompWarning: 10,
  clCompCritical: 15,
  clCompMinDuration: 0.25,       // seconds sustained before it counts
  clCompStableDelay: 0.6,        // seconds after launch/run-start before treating WOT as "stabilized"
  shiftRecoveryPct: 90,          // % of pre-shift RPM considered "recovered"
  fuelType: "auto",              // "auto", "gasoline_na", "gasoline_boosted", "e85", "e98", "methanol"
  bsfcOverride: null,            // lb/hp/hr - overrides the fuel-type default when set
};

function detectRateHz(channels, nRows) {
  // If a real rtc channel was populated (CSV path) with genuine deltas, derive
  // the true rate from it. An allocated-but-all-NaN rtc array (CSV lacked an
  // RTC column) must NOT be reported as exact - only real, valid deltas count.
  if (channels.rtc) {
    const d = [];
    for (let i = 1; i < nRows; i++) {
      const dt = channels.rtc[i] - channels.rtc[i - 1];
      if (dt > 0 && dt < 1) d.push(dt);
    }
    if (d.length > 10) return { rateHz: 1 / nanmedian(d), exact: true, source: "RTC" };
  }
  // .dl path: the Boost Time channel counts real seconds once the run starts,
  // so rows elapsed / seconds elapsed while it's counting gives the true
  // logging rate. Snapped to a whole Hz; only trusted if the timer ran for a
  // meaningful stretch and the result is a plausible Holley logging rate.
  const bt = channels.boost_time;
  if (bt) {
    let iStart = -1, iEnd = -1;
    for (let i = 1; i < nRows; i++) {
      if (iStart === -1 && bt[i - 1] <= 0.005 && bt[i] > 0.005) iStart = i;
      if (iStart !== -1 && bt[i] > bt[i - 1]) iEnd = i;
      if (iStart !== -1 && iEnd !== -1 && bt[i] < bt[i - 1] - 0.5) break; // timer reset
    }
    if (iStart !== -1 && iEnd - iStart >= 10) {
      const secs = bt[iEnd] - bt[iStart];
      if (secs > 0.4) {
        const hz = Math.round((iEnd - iStart) / secs);
        if (hz >= 5 && hz <= 200) return { rateHz: hz, exact: true, source: "boost timer" };
      }
    }
  }
  return { rateHz: ASSUMED_RATE_HZ, exact: false, source: "assumed" };
}

// Finds the wide-open-throttle run that is the actual pass and pads it by
// padSeconds on each side (clamped to the file bounds). Brief throttle dips
// (a pedal-out for tire shake, a TPS glitch) shorter than ~0.6s are bridged
// so one pass isn't split in two. When a trans-brake release or boost-timer
// start is logged, the run containing it wins; otherwise the longest run does
// (a burnout is usually shorter than the pass, but the launch marker is the
// reliable signal). Returns {start, end, rateHz, exact, wotStart, wotEnd}.
function findWOTWindow(channels, nRows, padSeconds = 5) {
  const { rateHz, exact, source } = detectRateHz(channels, nRows);
  const wot = [];
  for (let i = 0; i < nRows; i++) wot.push(channels.tps[i] > 85);
  const runs = [];
  let curStart = -1;
  for (let i = 0; i <= nRows; i++) {
    if (i < nRows && wot[i]) {
      if (curStart === -1) curStart = i;
    } else if (curStart !== -1) {
      runs.push([curStart, i - 1]);
      curStart = -1;
    }
  }
  if (!runs.length) return null;
  const maxGap = secondsToSamples(0.6, rateHz);
  const merged = [runs[0].slice()];
  for (const r of runs.slice(1)) {
    const last = merged[merged.length - 1];
    if (r[0] - last[1] - 1 <= maxGap) last[1] = r[1]; else merged.push(r.slice());
  }
  const hasLaunchMarker = (s, e) => {
    const tb = channels.trans_brake, bt = channels.boost_time;
    for (let i = Math.max(1, s); i <= e; i++) {
      if (tb && tb[i - 1] >= 0.5 && tb[i] < 0.5) return true;
      if (bt && bt[i - 1] <= 0.005 && bt[i] > 0.005) return true;
    }
    return false;
  };
  const withMarker = merged.filter(([s, e]) => hasLaunchMarker(s, e));
  const pool = withMarker.length ? withMarker : merged;
  const best = pool.reduce((a, b) => (b[1] - b[0] > a[1] - a[0] ? b : a));
  const bestStart = best[0], bestLen = best[1] - best[0] + 1;
  const pad = Math.round(padSeconds * rateHz);
  const start = Math.max(0, bestStart - pad);
  const end = Math.min(nRows - 1, bestStart + bestLen - 1 + pad);
  return { start, end, rateHz, exact, rateSource: source, wotStart: bestStart, wotEnd: bestStart + bestLen - 1 };
}

function sliceChannels(channels, start, end) {
  const out = {};
  for (const key of Object.keys(channels)) out[key] = channels[key].slice(start, end + 1);
  return out;
}

// Centered rolling median over the whole array (NaN-aware). Used to knock
// down single-sample sensor noise before a threshold is applied, so one bad
// sample can't trip a critical flag on its own.
function rollingMedian(arr, win) {
  const half = Math.max(0, Math.floor(win / 2));
  const out = new Float64Array(arr.length).fill(NaN);
  if (half === 0) { for (let i = 0; i < arr.length; i++) out[i] = arr[i]; return out; }
  for (let i = 0; i < arr.length; i++) {
    const seg = [];
    for (let k = Math.max(0, i - half); k <= Math.min(arr.length - 1, i + half); k++) if (!Number.isNaN(arr[k])) seg.push(arr[k]);
    if (seg.length) { seg.sort((a, b) => a - b); const m = Math.floor(seg.length / 2); out[i] = seg.length % 2 ? seg[m] : (seg[m - 1] + seg[m]) / 2; }
  }
  return out;
}

// Longest stretch of consecutive samples (by row index, so a gap in idxs -
// e.g. an excluded shift window - breaks the stretch) where test(i) is true.
// Returns {len, start, end} in row indices.
function longestRun(idxs, test) {
  let best = { len: 0, start: -1, end: -1 }, curStart = -1, curLen = 0, prev = -2;
  for (const i of idxs) {
    if (test(i)) {
      if (curLen > 0 && i === prev + 1) curLen++; else { curStart = i; curLen = 1; }
      if (curLen > best.len) best = { len: curLen, start: curStart, end: i };
    } else curLen = 0;
    prev = i;
  }
  return best;
}

// Builds the set of row indices that represent the actual pass: from the run
// start (trans-brake release / boost timer / two-step break) plus a short
// settle time, to the end of WOT, with TPS still at WOT, and with every
// detected shift window cut out. The two-step hold and shifts are where
// limiters cut fuel/spark and boost dips on purpose - judging AFR, boost
// control, oil or fuel pressure there flags normal behavior as a problem.
function buildPassIdx(sliced, win, segmentation, rateHz) {
  const localWotStart = win.wotStart - win.start, localWotEnd = win.wotEnd - win.start;
  const runIdx = segmentation && segmentation.runStart ? segmentation.runStart.idx : localWotStart;
  const settle = secondsToSamples(0.3, rateHz);
  const from = Math.min(localWotEnd, runIdx + settle);
  const excluded = new Uint8Array(localWotEnd + 1);
  const pre = secondsToSamples(0.1, rateHz), post = secondsToSamples(0.4, rateHz);
  for (const sh of (segmentation && segmentation.shifts) || []) {
    let recover = sh.dropIdx;
    const target = sh.rpmBefore * 0.9;
    while (recover < localWotEnd && sliced.rpm[recover] < target && recover - sh.dropIdx < secondsToSamples(2.5, rateHz)) recover++;
    for (let k = Math.max(0, sh.peakIdx - pre); k <= Math.min(localWotEnd, recover + post); k++) excluded[k] = 1;
  }
  const idxs = [];
  for (let i = from; i <= localWotEnd; i++) if (sliced.tps[i] > 85 && !excluded[i]) idxs.push(i);
  return { idxs, from, runIdx, excludedShiftSamples: excluded.reduce((a, b) => a + b, 0) };
}

function analyze(channels, nRows, rateHz = ASSUMED_RATE_HZ, coreRange = null, settings = DEFAULT_SETTINGS, passIdx = null) {
  const flags = [];
  // wotIdx must be restricted to the actual detected WOT run, not just any
  // sample above 85% TPS anywhere in the padded window. When the pass has been
  // segmented, only the pass itself (post-launch, shifts removed) is used.
  const [coreStart, coreEnd] = coreRange || [0, nRows - 1];
  const wot = Array.from({ length: nRows }, (_, i) => channels.tps[i] > 85 && i >= coreStart && i <= coreEnd);
  const allWotIdx = [...Array(nRows).keys()].filter((i) => wot[i]);
  const wotIdx = passIdx && passIdx.length >= secondsToSamples(0.75, rateHz) ? passIdx : allWotIdx;
  const pick = (arr, idxs) => idxs.map((i) => arr[i]);
  const sustainSamples = (sec) => secondsToSamples(sec, rateHz);

  // 1. Boost vs target - prefers a genuine boost-target channel; falls back to
  // dome pressure target (a proxy, not the same physical quantity, and clearly
  // labeled as such rather than silently treated as interchangeable).
  const directStatus = channels.boost_target_direct ? channelStatus(channels.boost_target_direct, { plausibleMax: 1 }) : "missing";
  const domeStatus = channelStatus(channels.boost_target, { plausibleMax: 1 });
  let targetArr = null, targetLabel = null, isProxy = false;
  if (directStatus === "available") {
    targetArr = channels.boost_target_direct; targetLabel = "Boost vs Target"; isProxy = false;
  } else if (domeStatus === "available" && domeLooksLikeBoostProxy(channels.boost_target, channels.boost_psig)) {
    targetArr = channels.boost_target; targetLabel = "Boost vs Dome Target"; isProxy = true;
  }
  if (targetArr && !isProxy) {
    // Real boost-target channel: boost should track it closely, 1:1.
    const err = wotIdx.map((i) => channels.boost_psig[i] - targetArr[i]);
    const bad = err.filter((e) => Math.abs(e) > settings.boostToleranceP).length;
    const frac = err.length ? bad / err.length : 0;
    if (frac > 0.15) {
      flags.push({
        name: targetLabel, sev: "warn", channel: "boost_psig", idxs: wotIdx,
        msg: `Boost off target by more than ${settings.boostToleranceP} psi for ${Math.round(frac * 100)}% of the pass (average error ${nanmean(err) >= 0 ? "+" : ""}${nanmean(err).toFixed(1)} psi).`,
      });
    } else {
      flags.push({ name: targetLabel, sev: "ok", channel: "boost_psig", idxs: [],
        msg: `Boost is tracking target closely through the pass.` });
    }
  } else if (targetArr && isProxy) {
    // Dome pressure proxy: a fixed gap between dome and actual boost is
    // normal (depends on wastegate spring/preload). What matters is whether
    // that gap stays CONSISTENT once both have settled. Dome is usually a
    // time-based curve that ramps up after launch, so only samples where the
    // dome command has reached its plateau AND boost has built up count -
    // during the ramp the offset shifts by design.
    const wotBoostPeak = nanmax(pick(channels.boost_psig, wotIdx));
    const domePeak = nanmax(pick(targetArr, wotIdx));
    // Settled = after boost first reaches 90% of its pass peak (the spool-up
    // ramp after launch is not a control inconsistency) and with the dome
    // command already at its plateau.
    const firstNear = wotIdx.findIndex((i) => channels.boost_psig[i] >= wotBoostPeak * 0.9);
    const stableIdx = (firstNear === -1 ? [] : wotIdx.slice(firstNear))
      .filter((i) => channels.boost_psig[i] >= wotBoostPeak * 0.6 && targetArr[i] >= domePeak - 1);
    const boostSm = rollingMedian(channels.boost_psig, sustainSamples(0.25));
    const offsets = stableIdx.map((i) => targetArr[i] - boostSm[i]);
    const medianOffset = nanmedian(offsets);
    const deviations = offsets.map((o) => Math.abs(o - medianOffset));
    const maxDeviation = deviations.length ? Math.max(...deviations) : 0;
    const badFrac = deviations.length ? deviations.filter((d) => d > settings.boostToleranceP).length / deviations.length : 0;
    const gapDesc = medianOffset >= 0 ? `${medianOffset.toFixed(0)} psi below dome` : `${Math.abs(medianOffset).toFixed(0)} psi above dome`;
    if (deviations.length < 5) {
      flags.push({ name: targetLabel, sev: "info", channel: "boost_psig", idxs: [],
        msg: "Not enough of the pass spent with dome pressure and boost both settled to judge dome-offset consistency, check not evaluated." });
    } else if (badFrac > 0.15) {
      flags.push({
        name: targetLabel, sev: "warn", channel: "boost_psig", idxs: stableIdx,
        msg: `Boost tracked dome pressure with an inconsistent offset once both settled, typically ${gapDesc} but drifting up to ${maxDeviation.toFixed(0)} psi from that for ${Math.round(badFrac * 100)}% of that time. A steady offset is normal and depends on wastegate spring preload, this much variation suggests the control isn't holding steady rather than a simple sizing gap.`,
      });
    } else {
      flags.push({ name: targetLabel, sev: "ok", channel: "boost_psig", idxs: [],
        msg: `Boost held a consistent offset to dome pressure once settled (${gapDesc}). A steady gap is normal and wastegate-spring-dependent, not a target miss.` });
    }
  } else {
    const domeMismatch = domeStatus === "available";
    flags.push({ name: "Boost vs Target", sev: "info", channel: "boost_psig", idxs: [],
      msg: domeMismatch
        ? "Dome pressure target is logged but tops out far below actual boost for this pull, doesn't look like a usable boost-target proxy, check not evaluated."
        : "No boost target or dome target channel logged for this car or tune, check not evaluated." });
  }

  // 2. Boost stability - judged on the pass after boost first gets close to
  // its peak, with shift windows already removed (a boost dip on a gear change
  // is normal). A "spike" is a departure from the local 0.5s trend, not just a
  // big sample-to-sample step, so a steadily climbing boost curve isn't
  // mistaken for instability. Neighbouring spike samples count as one event.
  const boostPass = pick(channels.boost_psig, wotIdx);
  const peakBoostWot = nanmax(boostPass);
  let rampEndPos = 0;
  for (let k = 0; k < wotIdx.length; k++) {
    if (boostPass[k] >= peakBoostWot * 0.85) { rampEndPos = k; break; }
  }
  const postRampIdx = wotIdx.slice(rampEndPos);
  if (postRampIdx.length < sustainSamples(0.75) || !(peakBoostWot > 3)) {
    flags.push({ name: "Boost Stability", sev: "info", channel: "boost_psig", idxs: [],
      msg: "Not enough time at boost after the initial ramp to judge stability, check not evaluated." });
  } else {
    const trend = rollingMedian(channels.boost_psig, sustainSamples(0.5));
    const resid = postRampIdx.map((i) => channels.boost_psig[i] - trend[i]);
    const absRes = resid.map(Math.abs).filter((v) => !Number.isNaN(v));
    const mad = nanmedian(absRes);
    const thresh = Math.max(2, 4 * mad * 1.4826);
    const spikeIdx = postRampIdx.filter((i, k) => Math.abs(resid[k]) > thresh);
    let events = 0, prev = -99;
    for (const i of spikeIdx) { if (i - prev > sustainSamples(0.2)) events++; prev = i; }
    if (events > 3) {
      flags.push({ name: "Boost Stability", sev: "warn", channel: "boost_psig", idxs: spikeIdx,
        msg: `${events} erratic boost swings (more than ${thresh.toFixed(1)} psi off the local trend) after the initial ramp, outside of gear changes.` });
    } else {
      flags.push({ name: "Boost Stability", sev: "ok", channel: "boost_psig", idxs: [],
        msg: "Boost trace is smooth once past the initial ramp, gear changes excluded." });
    }
  }

  // 3. AFR vs target. The wideband is smoothed (~0.25s rolling median) and
  // compared against target from ~0.1s earlier (sensor/transport lag), and a
  // lean condition has to be sustained before it's called critical - a single
  // noisy sample or a momentary transient isn't a lean pass.
  const afrStatus = channelStatus(channels.afr_avg, { plausibleMax: 3 });
  const targetAfrStatus = channelStatus(channels.target_afr, { plausibleMax: 3 });
  if (afrStatus !== "available" || targetAfrStatus !== "available") {
    flags.push({ name: "Air Fuel", sev: "info", channel: "afr_avg", idxs: [],
      msg: "AFR or target AFR channel not available for this log, check not evaluated." });
  } else {
    const lag = sustainSamples(0.1);
    const afrSm = rollingMedian(channels.afr_avg, sustainSamples(0.25));
    const tgt = (i) => channels.target_afr[Math.max(0, i - lag)];
    const errAt = (i) => afrSm[i] - tgt(i);
    const leanTol = settings.afrLeanTolerance;
    const valid = wotIdx.filter((i) => !Number.isNaN(afrSm[i]) && !Number.isNaN(tgt(i)));
    const leanIdx = valid.filter((i) => errAt(i) > leanTol);
    const leanFrac = valid.length ? leanIdx.length / valid.length : 0;
    const leanRun = longestRun(valid, (i) => errAt(i) > leanTol);
    const leanRunSec = leanRun.len / rateHz;
    const richTol = Math.max(leanTol * 2, 0.08 * nanmedian(valid.map(tgt)));
    const richRun = longestRun(valid, (i) => errAt(i) < -richTol);
    if (leanRunSec >= 0.3 || leanFrac > 0.1) {
      const bands = {};
      for (const i of valid) {
        const band = Math.floor(channels.rpm[i] / 500) * 500;
        (bands[band] ??= []).push(errAt(i));
      }
      const bandRows2 = Object.entries(bands)
        .filter(([, errs]) => errs.length >= sustainSamples(0.3))
        .map(([band, errs]) => ({ band: Number(band), medianErr: nanmedian(errs) }))
        .sort((a, b) => a.band - b.band);
      const leanBands = bandRows2.filter((b) => b.medianErr > leanTol);
      const sev = leanRunSec >= 0.3 ? "critical" : "warn";
      const worstErr = leanRun.len ? nanmax(pick(afrSm, [...Array(leanRun.len).keys()].map((k) => leanRun.start + k)).map((v, k) => v - tgt(leanRun.start + k))) : NaN;
      const where = leanRun.len
        ? ` Longest lean stretch ${leanRunSec.toFixed(2)}s around ${Math.round(channels.rpm[leanRun.start]).toLocaleString()}–${Math.round(channels.rpm[leanRun.end]).toLocaleString()} RPM, up to ${worstErr.toFixed(2)} AFR points over target.`
        : "";
      const bandTxt = leanBands.length
        ? `Lean of target between ${leanBands[0].band.toLocaleString()}–${(leanBands[leanBands.length - 1].band + 500).toLocaleString()} RPM`
        : `Lean of target in short bursts`;
      flags.push({ name: "Air Fuel", sev, channel: "afr_avg", idxs: leanIdx,
        msg: `${bandTxt} (${Math.round(leanFrac * 100)}% of the pass more than ${leanTol} AFR points lean, launch and shifts excluded).${where}` });
    } else if (richRun.len / rateHz >= 0.5) {
      flags.push({ name: "Air Fuel", sev: "warn", channel: "afr_avg", idxs: [],
        msg: `Running well rich of target for ${(richRun.len / rateHz).toFixed(2)}s around ${Math.round(channels.rpm[richRun.start]).toLocaleString()} RPM (more than ${richTol.toFixed(2)} AFR points rich). Not a safety issue on its own, but it's fuel and power left on the table, or a sign the fuel model is off.` });
    } else {
      flags.push({ name: "Air Fuel", sev: "ok", channel: "afr_avg", idxs: [],
        msg: "AFR is tracking target through the pass (launch and shifts excluded)." });
    }

    // 3b. Bank-to-bank AFR split. The average can look perfect while one bank
    // runs lean and the other rich - an injector, sensor, or (on twin-turbo
    // cars) turbo/wastegate imbalance. Easy to miss when only watching AFR avg.
    const lStatus = channelStatus(channels.afr_left, { plausibleMax: 3 });
    const rStatus = channelStatus(channels.afr_right, { plausibleMax: 3 });
    if (lStatus === "available" && rStatus === "available") {
      const lSm = rollingMedian(channels.afr_left, sustainSamples(0.25));
      const rSm = rollingMedian(channels.afr_right, sustainSamples(0.25));
      const splitPct = (i) => ((rSm[i] - lSm[i]) / tgt(i)) * 100;
      const sv = valid.filter((i) => !Number.isNaN(lSm[i]) && !Number.isNaN(rSm[i]));
      const splits = sv.map(splitPct);
      const medSplit = nanmedian(splits);
      const splitRun = longestRun(sv, (i) => Math.abs(splitPct(i)) > 6);
      const splitRunSec = splitRun.len / rateHz;
      if (sv.length && (Math.abs(medSplit) > 5 || splitRunSec >= 0.75)) {
        const leanerBank = medSplit > 0 ? "right" : "left";
        const medPts = nanmedian(sv.map((i) => rSm[i] - lSm[i]));
        flags.push({ name: "Bank-to-Bank AFR", sev: "warn", channel: "afr_left", idxs: [],
          msg: `The ${leanerBank} bank ran consistently leaner than the other during the pass, typically ${Math.abs(medPts).toFixed(2)} AFR points (${Math.abs(medSplit).toFixed(0)}%) apart${splitRunSec >= 0.75 ? `, over 6% apart for ${splitRunSec.toFixed(2)}s straight` : ""}. The average can look fine while one bank is lean. Worth checking injectors, wideband calibration, and on twin-turbo cars, boost balance side to side.` });
      } else if (sv.length) {
        flags.push({ name: "Bank-to-Bank AFR", sev: "ok", channel: "afr_left", idxs: [],
          msg: `Left and right banks stayed within ${Math.abs(medSplit).toFixed(0)}% of each other through the pass.` });
      }
    }
  }

  // 4. Oil pressure. Smoothed, and a low reading has to last ~0.25s to count,
  // so one noisy sample can't raise a critical flag. Also looks for two
  // patterns a floor check misses: pressure falling while RPM is still
  // climbing (oil temp, aeration, pump/pickup), and pressure swinging hard at
  // steady RPM (pickup starvation / aeration).
  const oilStatus = channelStatus(channels.oil_pressure, { plausibleMax: 5 });
  if (settings.oilPressureRule === "off") {
    flags.push({ name: "Oil Pressure", sev: "info", channel: "oil_pressure", idxs: [],
      msg: "Oil pressure rule disabled in settings, check not evaluated." });
  } else if (oilStatus !== "available") {
    flags.push({ name: "Oil Pressure", sev: "info", channel: "oil_pressure", idxs: [],
      msg: "Oil pressure channel not available for this log, check not evaluated." });
  } else {
    const oilSm = rollingMedian(channels.oil_pressure, sustainSamples(0.2));
    const floorFor = settings.oilPressureRule === "flat"
      ? () => settings.oilPressureFlatMin
      : (i) => channels.rpm[i] * settings.oilPressureRatio / 1000;
    const lowTest = (i) => oilSm[i] < floorFor(i);
    const lowOilIdx = wotIdx.filter(lowTest);
    const lowRun = longestRun(wotIdx, lowTest);
    const lowRunSec = lowRun.len / rateHz;
    let flagged = false;
    if (lowRunSec >= 0.25) {
      const minOil = nanmin(pick(oilSm, lowOilIdx));
      const minAt = lowOilIdx.find((i) => oilSm[i] === minOil);
      const ruleDesc = settings.oilPressureRule === "flat"
        ? `below the configured ${settings.oilPressureFlatMin} psi minimum`
        : `below ${settings.oilPressureRatio} psi per 1000 RPM`;
      flags.push({ name: "Oil Pressure", sev: "critical", channel: "oil_pressure", idxs: lowOilIdx,
        msg: `Oil pressure ${ruleDesc} for ${(lowOilIdx.length / rateHz).toFixed(2)}s of the pass (longest stretch ${lowRunSec.toFixed(2)}s), low of ${minOil.toFixed(0)} psi at ${Math.round(channels.rpm[minAt]).toLocaleString()} RPM.` });
      flagged = true;
    }
    // Falling while RPM rises: compare the pass's peak (smoothed) pressure to
    // the lowest pressure seen later in the pass at equal-or-higher RPM.
    let peakK = 0;
    const oilPass = pick(oilSm, wotIdx);
    for (let k = 1; k < oilPass.length; k++) if (oilPass[k] > oilPass[peakK]) peakK = k;
    const peakOil = oilPass[peakK], peakRpm = channels.rpm[wotIdx[peakK]];
    let fallK = -1;
    for (let k = peakK + 1; k < wotIdx.length; k++) {
      if (channels.rpm[wotIdx[k]] >= peakRpm && (fallK === -1 || oilPass[k] < oilPass[fallK])) fallK = k;
    }
    if (fallK !== -1 && peakOil - oilPass[fallK] >= Math.max(15, peakOil * 0.2)) {
      flags.push({ name: "Oil Pressure Trend", sev: "warn", channel: "oil_pressure", idxs: wotIdx.slice(peakK, fallK + 1),
        msg: `Oil pressure fell from ${peakOil.toFixed(0)} psi at ${Math.round(peakRpm).toLocaleString()} RPM to ${oilPass[fallK].toFixed(0)} psi at ${Math.round(channels.rpm[wotIdx[fallK]]).toLocaleString()} RPM, dropping while RPM kept climbing. Pressure should hold or rise with RPM, a drop like this usually points to rising oil temperature, aeration, or pump/pickup issues.` });
      flagged = true;
    }
    // Unstable: rolling range of the raw signal over ~0.5s at WOT.
    const w = sustainSamples(0.5);
    const unstableTest = (i) => {
      let lo = Infinity, hi = -Infinity;
      for (let k = Math.max(0, i - w); k <= i; k++) { const v = channels.oil_pressure[k]; if (!Number.isNaN(v)) { lo = Math.min(lo, v); hi = Math.max(hi, v); } }
      return hi - lo > Math.max(20, 0.25 * hi);
    };
    const unstableIdx = wotIdx.filter(unstableTest);
    const unstableRun = longestRun(wotIdx, unstableTest);
    if (unstableRun.len / rateHz >= 0.5) {
      const seg = Array.from(channels.oil_pressure.slice(Math.max(0, unstableRun.start - w), unstableRun.end + 1)).filter((v) => !Number.isNaN(v));
      flags.push({ name: "Oil Pressure Stability", sev: "warn", channel: "oil_pressure", idxs: unstableIdx,
        msg: `Oil pressure was swinging between ${Math.min(...seg).toFixed(0)} and ${Math.max(...seg).toFixed(0)} psi for ${(unstableRun.len / rateHz).toFixed(2)}s around ${Math.round(channels.rpm[unstableRun.start]).toLocaleString()}–${Math.round(channels.rpm[unstableRun.end]).toLocaleString()} RPM. Rapid swings at steady RPM are a common sign of oil aeration or the pickup uncovering, worth checking oil level and pan/pickup setup, or the sender and its wiring.` });
      flagged = true;
    }
    if (!flagged) {
      flags.push({ name: "Oil Pressure", sev: "ok", channel: "oil_pressure", idxs: [],
        msg: "Oil pressure stayed above a safe floor and held steady through the pass." });
    }
  }

  // 5. Fuel pressure tracking boost 1:1. The reference is fuel pressure minus
  // boost measured in the lower part of the boost range (where the pump has
  // plenty of margin), so idle vacuum and engine-off samples never enter the
  // baseline. The expected pressure at any point is that reference + boost;
  // a shortfall has to be sustained to count.
  const fpStatus = channelStatus(channels.fuel_pressure, { plausibleMax: 5 });
  if (fpStatus !== "available") {
    flags.push({ name: "Fuel Pressure", sev: "info", channel: "fuel_pressure", idxs: [],
      msg: "Fuel pressure channel not available for this log, check not evaluated." });
  } else {
    const fpSm = rollingMedian(channels.fuel_pressure, sustainSamples(0.2));
    const bSm = rollingMedian(channels.boost_psig, sustainSamples(0.2));
    const boostPeak = nanmax(pick(bSm, allWotIdx));
    let refIdx = allWotIdx.filter((i) => bSm[i] > -1 && bSm[i] <= Math.max(3, boostPeak * 0.5));
    if (refIdx.length < sustainSamples(0.5)) refIdx = allWotIdx;
    const ref = nanmedian(refIdx.map((i) => fpSm[i] - Math.max(bSm[i], 0)));
    const deficitAt = (i) => (ref + Math.max(bSm[i], 0)) - fpSm[i];
    const deficits = wotIdx.map(deficitAt).filter((v) => !Number.isNaN(v));
    const maxDef = deficits.length ? Math.max(...deficits) : 0;
    const critRun = longestRun(wotIdx, (i) => deficitAt(i) > 8);
    const warnRun = longestRun(wotIdx, (i) => deficitAt(i) > 5);
    if (critRun.len / rateHz >= 0.25 || warnRun.len / rateHz >= 0.5) {
      const sev = critRun.len / rateHz >= 0.25 ? "critical" : "warn";
      const run = sev === "critical" ? critRun : warnRun;
      const worstIdx = wotIdx.filter((i) => deficitAt(i) > 5);
      flags.push({ name: "Fuel Pressure", sev, channel: "fuel_pressure", idxs: worstIdx,
        msg: `Fuel pressure fell behind boost, up to ${maxDef.toFixed(0)} psi short of a 1:1 rise (base about ${ref.toFixed(0)} psi over boost), sustained for ${(run.len / rateHz).toFixed(2)}s from ${Math.round(channels.rpm[run.start]).toLocaleString()} RPM. Common causes are pump capacity, voltage to the pump, or a restriction or regulator issue.` });
    } else {
      flags.push({ name: "Fuel Pressure", sev: "ok", channel: "fuel_pressure", idxs: [],
        msg: `Fuel pressure is tracking boost one to one (holding about ${ref.toFixed(0)} psi over boost).` });
    }
  }

  // 6. Temps - compares roughly the first and last second of the window rather
  // than a fixed sample count, so it behaves the same at any logging rate.
  for (const [key, label, limit] of [["cts", "Coolant Temp", settings.coolantTempLimit], ["mat", "Intake Air Temp", settings.intakeTempLimit]]) {
    const status = channelStatus(channels[key], { minFinitePct: 0.5 });
    if (status !== "available") {
      flags.push({ name: label, sev: "info", channel: key, idxs: [],
        msg: `${label} channel not available for this log, check not evaluated.` });
      continue;
    }
    const s = channels[key];
    const win = secondsToSamples(1, rateHz);
    const head = Array.from(s.slice(0, win));
    const tail = Array.from(s.slice(-win));
    const rise = nanmean(tail) - nanmean(head);
    const peak = nanmax(s);
    let flagged = false;
    if (rise > 15) {
      flags.push({ name: label, sev: "warn", channel: key, idxs: [],
        msg: `${label} climbed ${rise.toFixed(0)} degrees across the log.` });
      flagged = true;
    }
    if (peak > limit) {
      flags.push({ name: label, sev: "warn", channel: key, idxs: [],
        msg: `${label} peaked at ${peak.toFixed(0)} degrees.` });
      flagged = true;
    }
    if (!flagged) {
      flags.push({ name: label, sev: "ok", channel: key, idxs: [],
        msg: `${label} stayed within a normal range.` });
    }
  }

  // 7. Battery voltage during the run. Many race cars run total-loss (no
  // alternator), so "not charging" isn't a fault by itself. What matters is
  // how far voltage sags under the load of the pass (pumps, fans, ignition)
  // and whether it gets low enough to affect injector timing and pump output.
  const battStatus = channelStatus(channels.battery, { plausibleMax: 3 });
  if (battStatus !== "available") {
    flags.push({ name: "Charging System", sev: "info", channel: "battery", idxs: [],
      msg: "Battery voltage channel not available for this log, check not evaluated." });
  } else {
    const vSm = rollingMedian(channels.battery, sustainSamples(0.2));
    const preIdx = [];
    for (let i = Math.max(0, coreStart - secondsToSamples(2, rateHz)); i < coreStart; i++) if (channels.rpm[i] > 500) preIdx.push(i);
    const startRef = preIdx.length >= 3 ? nanmedian(pick(vSm, preIdx)) : nanmedian(pick(vSm, allWotIdx.slice(0, secondsToSamples(0.5, rateHz))));
    const vMin = nanmin(pick(vSm, allWotIdx));
    const sag = startRef - vMin;
    let flagged = false;
    if (vMin < 12.0) {
      flags.push({ name: "Charging System", sev: "warn", channel: "battery", idxs: [],
        msg: `Voltage dropped to ${vMin.toFixed(1)}V during the pass. Below about 12V, fuel pump output and injector opening time start to suffer, check battery charge, cabling, and grounds.` });
      flagged = true;
    } else if (sag > 1.0) {
      flags.push({ name: "Charging System", sev: "warn", channel: "battery", idxs: [],
        msg: `Voltage sagged ${sag.toFixed(1)}V under load (from ${startRef.toFixed(1)}V to ${vMin.toFixed(1)}V) during the pass. Worth checking battery condition, cabling and grounds.` });
      flagged = true;
    }
    if (!flagged) {
      flags.push({ name: "Charging System", sev: "ok", channel: "battery", idxs: [],
        msg: `Voltage held steady during the pass (${vMin.toFixed(1)}–${nanmax(pick(vSm, allWotIdx)).toFixed(1)}V).` });
    }
  }

  return flags;
}

// ============================================================
// Deeper metrics, computed on the WOT-only portion of the trimmed window
// (win.wotStart/win.wotEnd are absolute row indices; localWot* below are
// re-based to the sliced array that analyzeWOT hands around).
// ============================================================

// Finds where the driver is standing on the two-step: RPM held roughly flat
// against the launch rev limiter (despite WOT) before it breaks free and climbs.
// Only searches the first 70% of the WOT segment, so it can't accidentally grab
// a flat spot late in the pull. Returns {start, end} (both local indices), or
// null if no plateau is found (e.g. a roll-on pull with no two-step launch).
function findTwoStepHold(rpm, localWotStart, localWotEnd, rateHz = ASSUMED_RATE_HZ) {
  const n = localWotEnd - localWotStart + 1;
  // A two-step hold happens right as the driver plants the throttle, not
  // partway through the pull (a mid-pull flat spot is more likely a shift
  // point), so only look at the first several seconds of WOT.
  const searchEnd = localWotStart + Math.min(Math.floor(n * 0.6), secondsToSamples(7.5, rateHz));
  const win = secondsToSamples(0.6, rateHz);
  const flatThresh = 200; // rpm range allowed within a "flat" window (rev limiters bounce a bit)
  const minPlateauSamples = secondsToSamples(0.6, rateHz); // require at least ~0.6s held before it counts
  const maxOnsetDelay = secondsToSamples(4.5, rateHz); // plateau must start within ~4.5s of WOT onset (some cars ramp up to the limiter gradually)
  let bestStart = -1, bestLen = 0, curStart = -1;
  for (let i = localWotStart; i <= searchEnd - win; i++) {
    const seg = rpm.slice(i, i + win);
    const lo = Math.min(...seg), hi = Math.max(...seg);
    if (hi - lo <= flatThresh) {
      if (curStart === -1) curStart = i;
    } else if (curStart !== -1) {
      const len = i - curStart;
      if (len > bestLen) { bestLen = len; bestStart = curStart; }
      curStart = -1;
    }
  }
  if (curStart !== -1) {
    const len = searchEnd - curStart;
    if (len > bestLen) { bestLen = len; bestStart = curStart; }
  }
  if (bestLen < minPlateauSamples || (bestStart - localWotStart) > maxOnsetDelay) return null;
  // A real two-step hold bounces against the limiter (RPM goes up and down);
  // a smooth deceleration-rate climb near a shift point just flattens out
  // monotonically. Require some genuine back-and-forth before accepting it.
  const plateau = rpm.slice(bestStart, bestStart + bestLen);
  let negDeltas = 0;
  for (let i = 1; i < plateau.length; i++) if (plateau[i] < plateau[i - 1]) negDeltas++;
  if (negDeltas / (plateau.length - 1) < 0.2) return null;
  return { start: bestStart, end: bestStart + bestLen - 1 };
}

// Checks that RPM was actually being held against a limiter in the lead-up to
// a candidate release point (bouncing, not a genuine continuous climb that
// just happens to look locally flat for a few samples).
function heldBeforeRelease(rpm, idx, localWotStart, rateHz = ASSUMED_RATE_HZ) {
  const lookback = secondsToSamples(2, rateHz);
  const from = Math.max(localWotStart, idx - lookback);
  const seg = rpm.slice(from, idx);
  if (seg.length < 10) return false;
  const lo = Math.min(...seg), hi = Math.max(...seg);
  if (hi - lo > 800) return false; // too much net movement to be a hold
  let negDeltas = 0;
  for (let i = 1; i < seg.length; i++) if (seg[i] < seg[i - 1]) negDeltas++;
  return negDeltas / (seg.length - 1) >= 0.15; // genuine bounce, not a smooth climb
}

// A trans-brake release is a hard, logged launch marker - it doesn't need
// the RPM trace to look like a textbook limiter bounce (bump-box launches,
// timer-based launch RPM ramps and stall-converter cars all move RPM around
// on the brake). Just require the button to have actually been held for a
// moment at WOT, which filters out a single-sample glitch.
function transBrakeHeld(sliced, idx, localWotStart, rateHz = ASSUMED_RATE_HZ) {
  const tb = sliced.trans_brake, need = secondsToSamples(0.3, rateHz);
  let held = 0;
  for (let k = idx - 1; k >= localWotStart && tb[k] >= 0.5; k--) held++;
  return held >= need && sliced.tps[idx] > 85;
}

// Finds the moment the run actually starts, for lining up two logs on the
// same clock. Tries, in order: a trans-brake release (1 -> 0), a boost timer
// starting to count up from zero, then the RPM breaking free of a two-step
// hold. Falls back to the start of the WOT window if none of those are found.
function findRunStart(sliced, localWotStart, localWotEnd, rateHz = ASSUMED_RATE_HZ) {
  const rpm = sliced.rpm;
  const tb = sliced.trans_brake;
  if (tb) {
    for (let i = localWotStart + 1; i <= localWotEnd; i++) {
      if (!Number.isNaN(tb[i - 1]) && !Number.isNaN(tb[i]) && tb[i - 1] >= 0.5 && tb[i] < 0.5 &&
          transBrakeHeld(sliced, i, localWotStart, rateHz)) {
        return { idx: i, source: "trans brake release" };
      }
    }
  }
  const bt = sliced.boost_time;
  if (bt) {
    for (let i = localWotStart + 1; i <= localWotEnd; i++) {
      if (!Number.isNaN(bt[i - 1]) && !Number.isNaN(bt[i]) && bt[i - 1] <= 0.005 && bt[i] > 0.01 &&
          sliced.tps[i] > 85) {
        return { idx: i, source: "boost timer start" };
      }
    }
  }
  const hold = findTwoStepHold(rpm, localWotStart, localWotEnd, rateHz);
  if (hold) return { idx: hold.end + 1, source: "RPM breaking free of the two-step" };
  return { idx: localWotStart, source: "throttle onset (no launch marker found)" };
}

// Finds the start of the two-step hold using the trans-brake channel when
// it's available and a release actually validates (mirrors findRunStart's
// logic), by scanning back from the release to where the button was first
// held AND the driver was already at wide-open-throttle - spool only starts
// once both are true together, not just whichever came first.
function twoStepStartFromTransBrake(sliced, localWotStart, localWotEnd, rateHz = ASSUMED_RATE_HZ) {
  const tb = sliced.trans_brake, rpm = sliced.rpm, tps = sliced.tps;
  if (!tb) return null;
  for (let i = localWotStart + 1; i <= localWotEnd; i++) {
    if (!Number.isNaN(tb[i - 1]) && !Number.isNaN(tb[i]) && tb[i - 1] >= 0.5 && tb[i] < 0.5 &&
        transBrakeHeld(sliced, i, localWotStart, rateHz)) {
      let start = i - 1;
      while (start > localWotStart && tb[start - 1] >= 0.5 && tps[start - 1] > 85) start--;
      return start;
    }
  }
  return null;
}

// Finds the first point where boost has stopped climbing meaningfully for a
// sustained window - used as an alternate "spool complete" signal for cars
// where boost settles below its dome/target number by design (wastegate
// spring preload) and would otherwise never cross a fixed percentage.
// Finds the first point where BOTH boost and RPM have settled into a lasting
// plateau together, bounded to end by endIdx (the release point, when known)
// - spool is about the two-step hold settling, not the post-launch climb to
// full boost, which is a separate thing and can still be climbing well after
// the driver is already staged and ready.
function findSpoolStabilized(boost, rpm, startIdx, endIdx, rateHz) {
  const win = secondsToSamples(0.4, rateHz);
  const boostFlatThresh = 2, rpmFlatThresh = 200;
  const boostBaseline = boost[startIdx], rpmBaseline = rpm[startIdx];
  const boostPeak = nanmax(Array.from(boost.slice(startIdx, endIdx + 1)));
  const boostRiseNeeded = Math.max(2, (boostPeak - boostBaseline) * 0.4);
  for (let i = startIdx; i <= endIdx - win; i++) {
    const bSeg = Array.from(boost.slice(i, i + win)).filter(v => !Number.isNaN(v));
    const rSeg = Array.from(rpm.slice(i, i + win)).filter(v => !Number.isNaN(v));
    if (bSeg.length < win * 0.5 || rSeg.length < win * 0.5) continue;
    const bLo = Math.min(...bSeg), bHi = Math.max(...bSeg);
    const rLo = Math.min(...rSeg), rHi = Math.max(...rSeg);
    const bMean = nanmean(bSeg);
    if (bHi - bLo > boostFlatThresh || (bMean - boostBaseline) < boostRiseNeeded) continue;
    if (rHi - rLo > rpmFlatThresh) continue;
    // Lasting through the rest of the hold, not just a momentary lull.
    const bRest = Array.from(boost.slice(i + win, endIdx + 1)).filter(v => !Number.isNaN(v));
    const bLaterPeak = bRest.length ? nanmax(bRest) : bMean;
    if (bLaterPeak - bMean > boostFlatThresh * 1.5) continue;
    return i;
  }
  return null;
}

function computeSpoolTime(sliced, localWotStart, localWotEnd, rateHz) {
  const boost = sliced.boost_psig, rpm = sliced.rpm, tb = sliced.trans_brake;

  const tbStart = twoStepStartFromTransBrake(sliced, localWotStart, localWotEnd, rateHz);
  const hold = tbStart === null ? findTwoStepHold(rpm, localWotStart, localWotEnd, rateHz) : null;
  const startIdx = tbStart !== null ? tbStart : (hold ? hold.start : localWotStart);
  const fromTwoStep = tbStart !== null || !!hold;

  // Spool is about the two-step hold itself - bound the search to end at the
  // actual release, not the whole wide-open-throttle run. Boost commonly
  // keeps climbing well past release toward a higher final number; that's a
  // different thing (see Boost Fade / peak boost) from being staged and ready.
  let releaseIdx = localWotEnd;
  if (tb) {
    for (let i = startIdx + 1; i <= localWotEnd; i++) {
      if (!Number.isNaN(tb[i - 1]) && !Number.isNaN(tb[i]) && tb[i - 1] >= 0.5 && tb[i] < 0.5) { releaseIdx = i; break; }
    }
  } else if (hold) {
    releaseIdx = hold.end;
  }
  const searchEnd = Math.min(releaseIdx, localWotEnd);
  if (searchEnd - startIdx < 3) return null;

  const wotDirect = sliced.boost_target_direct ? sliced.boost_target_direct.slice(startIdx, searchEnd + 1) : null;
  let goal = null;
  if (wotDirect && channelStatus(wotDirect, { plausibleMax: 1 }) === "available") {
    goal = nanmedian(Array.from(wotDirect.slice(Math.floor(wotDirect.length / 2))));
  }

  let reachedIdx = null;
  if (goal !== null && goal > 1) {
    const thresh = goal * 0.9;
    for (let i = startIdx; i <= searchEnd; i++) {
      if (boost[i] >= thresh) { reachedIdx = i; break; }
    }
  }
  const stabilizedIdx = findSpoolStabilized(boost, rpm, startIdx, searchEnd, rateHz);

  let endIdx = null, viaStabilize = false;
  if (reachedIdx !== null && (stabilizedIdx === null || reachedIdx <= stabilizedIdx)) {
    endIdx = reachedIdx;
  } else if (stabilizedIdx !== null) {
    endIdx = stabilizedIdx; viaStabilize = true;
  }

  if (endIdx !== null) {
    const win = secondsToSamples(0.4, rateHz);
    const plateauLevel = nanmean(Array.from(boost.slice(endIdx, Math.min(endIdx + win, searchEnd + 1))));
    return { seconds: (endIdx - startIdx) / rateHz, goal, reached: true, viaStabilize, fromTwoStep, plateauLevel, startIdx };
  }
  return { seconds: null, goal, reached: false, fromTwoStep, plateauLevel: null, startIdx };
}

function computeBoostFade(sliced, localWotStart, localWotEnd) {
  const boostFull = sliced.boost_psig;
  const boost = boostFull.slice(localWotStart, localWotEnd + 1);
  const directArr = sliced.boost_target_direct;
  const domeArr = sliced.boost_target;
  const direct = directArr ? directArr.slice(localWotStart, localWotEnd + 1) : null;
  const dome = domeArr.slice(localWotStart, localWotEnd + 1);
  const n = boost.length;
  if (n < 9) return null;
  const third = Math.floor(n / 3);
  const firstBoost = boost.slice(0, third), lastBoost = boost.slice(n - third);
  let target = null;
  if (direct && channelStatus(direct, { plausibleMax: 1 }) === "available") {
    target = direct;
  } else if (channelStatus(dome, { plausibleMax: 1 }) === "available" && domeLooksLikeBoostProxy(domeArr, boostFull)) {
    target = dome;
  }
  if (target) {
    const firstErr = nanmean(Array.from(firstBoost).map((v, i) => v - target[i]));
    const lastErr = nanmean(Array.from(lastBoost).map((v, i) => v - target[n - third + i]));
    return { metric: "vs target", fadePsi: lastErr - firstErr, firstErr, lastErr };
  } else {
    const firstAvg = nanmean(Array.from(firstBoost));
    const lastAvg = nanmean(Array.from(lastBoost));
    return { metric: "level", fadePsi: lastAvg - firstAvg, firstErr: firstAvg, lastErr: lastAvg };
  }
}

function computeAfrByBand(sliced, localWotStart, localWotEnd, rateHz = ASSUMED_RATE_HZ) {
  // Skip the first ~0.75s of WOT: acceleration enrichment on tip-in is expected
  // behavior, not a lean condition worth flagging, and would otherwise dominate
  // the low-RPM bands.
  const tipInSkip = secondsToSamples(0.75, rateHz);
  const start = Math.min(localWotStart + tipInSkip, localWotEnd);
  const rpm = sliced.rpm.slice(start, localWotEnd + 1);
  const afr = sliced.afr_avg.slice(start, localWotEnd + 1);
  const target = sliced.target_afr.slice(start, localWotEnd + 1);
  const bands = {};
  for (let i = 0; i < rpm.length; i++) {
    if (Number.isNaN(rpm[i]) || Number.isNaN(afr[i]) || Number.isNaN(target[i])) continue;
    const band = Math.floor(rpm[i] / 500) * 500;
    if (!bands[band]) bands[band] = [];
    bands[band].push(afr[i] - target[i]);
  }
  const rows = Object.entries(bands)
    .filter(([, errs]) => errs.length >= 8)
    .map(([band, errs]) => ({ band: Number(band), n: errs.length, medianErr: nanmedian(errs) }))
    .sort((a, b) => a.band - b.band);
  return rows;
}

function computeDutyCeiling(sliced, localWotStart, localWotEnd, settings = DEFAULT_SETTINGS) {
  const dc = sliced.duty_cycle.slice(localWotStart, localWotEnd + 1);
  const clean = Array.from(dc).filter(v => !Number.isNaN(v));
  if (!clean.length) return null;
  const pinned = clean.filter(v => v > settings.dutyCycleCritical).length;
  return { max: nanmax(clean), pinnedFrac: pinned / clean.length };
}

// Looks for gear shifts: a fast RPM drop (not a throttle lift - TPS must stay
// at WOT throughout) that's followed by RPM climbing again afterward, which
// rules out ordinary two-step bounce or sensor noise. Tuned conservatively -
// checked against all five real logs on hand and found zero false positives,
// but none of those logs contain a confirmed real shift either, so this
// hasn't been validated against a true positive yet. Treat "0 shifts found"
// as "no shift detected," not proof the pull was single-gear.
//
// For each shift, reports rich metrics using every channel available: robust
// shift RPM (median over the ~100ms before the drop, not one noisy sample),
// drop size and duration, recovery timing, whether the driver lifted, boost
// loss/recovery, AFR movement, and CL Comp movement. Limiter contact right
// before the shift is only ever reported as a hedge ("may indicate"), never
// asserted outright, since it's inferred from RPM shape alone.
function detectShifts(sliced, startIdx, endIdx, rateHz, settings = DEFAULT_SETTINGS) {
  const rpm = sliced.rpm, tps = sliced.tps;
  const fastWin = secondsToSamples(0.5, rateHz);      // window to find the true trough (real drops can take ~0.3-0.4s to fully bottom out)
  const confirmWin = secondsToSamples(1.0, rateHz);    // window to confirm the RPM trend has genuinely reversed
  const confirmThresh = 100;                           // rpm gain within confirmWin that counts as "recovering" (real recoveries can be gradual)
  const reportWin = secondsToSamples(2.5, rateHz);     // longer horizon purely for reporting recovery-timing metrics
  const skipAhead = secondsToSamples(0.6, rateHz);     // resume scanning soon after a shift, so a second shift close behind isn't skipped over
  const rpmWin = Math.max(2, secondsToSamples(0.1, rateHz)); // window for robust shift-RPM
  const shifts = [];
  let i = startIdx;
  while (i <= endIdx - fastWin) {
    if (rpm[i] < 3000 || tps[i] < 85) { i++; continue; }
    // Track the RPM minimum through the drop window even if TPS dips - a
    // lift-shift should still be detected and labeled, not thrown out. The
    // real filter against a genuine end-of-run lift is the recovery check
    // below (RPM has to actually trend back up again, which a coast-down
    // to idle never does).
    let minV = rpm[i], minJ = i;
    for (let k = i; k < Math.min(i + fastWin, endIdx + 1); k++) {
      if (rpm[k] < minV) { minV = rpm[k]; minJ = k; }
    }
    if (rpm[i] - minV < 400) { i++; continue; }
    // Use the true local peak within the window as the shift reference, not
    // just the first row that happened to qualify - a wide fastWin can catch
    // a still-climbing sample before the actual top of the rev.
    let peakI = i;
    for (let k = i; k < minJ; k++) {
      if (rpm[k] > rpm[peakI] && rpm[k] - minV >= 400) peakI = k;
    }
    i = peakI;
    let recovered = false, recoverIdx = null;
    for (let k = minJ; k < Math.min(minJ + confirmWin, endIdx + 1); k++) {
      if (rpm[k] > minV + confirmThresh) { recovered = true; recoverIdx = k; break; }
    }
    if (!recovered) { i++; continue; }

    // Robust shift RPM: median over the short window ending at the peak,
    // not the single (possibly noisy) sample at the peak index.
    const preWinStart = Math.max(startIdx, i - rpmWin + 1);
    const rpmBefore = nanmedian(Array.from(rpm.slice(preWinStart, i + 1)));

    // Limiter hint: look for oscillation (bounce) in the ~0.4s right before
    // the peak - similar signature to the two-step hold, just at higher RPM.
    const limWin = secondsToSamples(0.4, rateHz);
    const limStart = Math.max(startIdx, i - limWin);
    const limSeg = Array.from(rpm.slice(limStart, i + 1));
    let limiterHint = null;
    if (limSeg.length >= 6) {
      const lo = Math.min(...limSeg), hi = Math.max(...limSeg);
      let negDeltas = 0;
      for (let k = 1; k < limSeg.length; k++) if (limSeg[k] < limSeg[k - 1]) negDeltas++;
      if (hi - lo < 300 && negDeltas / (limSeg.length - 1) >= 0.25) {
        limiterHint = `RPM behavior before the shift may indicate brief limiter contact (roughly ${(limSeg.length / rateHz).toFixed(2)}s).`;
      }
    }

    // Time until RPM starts climbing again vs. time to recover to a
    // configured percentage of the pre-shift RPM (separate milestones,
    // searched over the longer reporting horizon since real recoveries
    // can be gradual, this is informational and doesn't gate detection).
    const climbSec = (recoverIdx - minJ) / rateHz;
    const recoveryTargetRpm = rpmBefore * (settings.shiftRecoveryPct ?? 90) / 100;
    let recoverToPctIdx = null;
    for (let k = minJ; k <= Math.min(minJ + reportWin, endIdx); k++) {
      if (rpm[k] >= recoveryTargetRpm) { recoverToPctIdx = k; break; }
    }
    const recoverToPctSec = recoverToPctIdx !== null ? (recoverToPctIdx - minJ) / rateHz : null;

    // Boost, AFR, CL Comp movement across the shift window, when available.
    let boostBefore = null, boostMin = null, boostRecoverSec = null;
    if (channelStatus(sliced.boost_psig, { minFinitePct: 0.5 }) === "available") {
      boostBefore = sliced.boost_psig[i];
      let bMin = boostBefore, bMinIdx = i;
      for (let k = i; k <= Math.min(minJ + confirmWin, endIdx); k++) {
        if (sliced.boost_psig[k] < bMin) { bMin = sliced.boost_psig[k]; bMinIdx = k; }
      }
      boostMin = bMin;
      for (let k = bMinIdx; k <= Math.min(bMinIdx + reportWin, endIdx); k++) {
        if (sliced.boost_psig[k] >= boostBefore - 1) { boostRecoverSec = (k - bMinIdx) / rateHz; break; }
      }
    }
    let afrShift = null;
    if (channelStatus(sliced.afr_avg, { plausibleMax: 3 }) === "available" && channelStatus(sliced.target_afr, { plausibleMax: 3 }) === "available") {
      const before = nanmean(Array.from(sliced.afr_avg.slice(Math.max(startIdx, i - rpmWin), i)).map((v, idx2) => v - sliced.target_afr[Math.max(startIdx, i - rpmWin) + idx2]));
      const after = nanmean(Array.from(sliced.afr_avg.slice(minJ, Math.min(endIdx, minJ + confirmWin))).map((v, idx2) => v - sliced.target_afr[minJ + idx2]));
      if (!Number.isNaN(before) && !Number.isNaN(after)) afrShift = after - before;
    }
    let clShift = null;
    if (sliced.cl_comp && channelStatus(sliced.cl_comp, { minFinitePct: 0.5 }) === "available") {
      const before = nanmean(Array.from(sliced.cl_comp.slice(Math.max(startIdx, i - rpmWin), i)));
      const after = nanmean(Array.from(sliced.cl_comp.slice(minJ, Math.min(endIdx, minJ + confirmWin))));
      if (!Number.isNaN(before) && !Number.isNaN(after)) clShift = after - before;
    }

    const tpsDuring = nanmean(Array.from(tps.slice(i, minJ + 1)));
    const lifted = tpsDuring < 96;

    shifts.push({
      peakIdx: i, dropIdx: minJ, rpmBefore, rpmAfter: minV,
      rpmDrop: rpmBefore - minV, rpmDropPct: (rpmBefore - minV) / rpmBefore * 100,
      durationSec: (minJ - i) / rateHz, climbSec, recoverToPctSec,
      tpsDuring, lifted, limiterHint,
      boostBefore, boostMin, boostLoss: boostBefore !== null ? boostBefore - boostMin : null, boostRecoverSec,
      afrShift, clShift,
      confidence: lifted ? "low" : "medium",
    });
    i = minJ + skipAhead;
  }
  return shifts;
}

// Breaks the padded analysis window into named phases: lead-in, throttle
// application (including a two-step hold if one was found), launch, gear
// sections split at any detected shifts, and the cool-down/lift after WOT
// ends. Segmentation confidence is "medium" when a launch marker (trans
// brake, boost timer, or a validated two-step) was actually found, "low"
// when everything fell back to guesses (e.g. a roll-on pull).
function segmentRun(sliced, win, rateHz, settings = DEFAULT_SETTINGS) {
  const localWotStart = win.wotStart - win.start;
  const localWotEnd = win.wotEnd - win.start;
  const totalRows = win.end - win.start + 1;
  const rpm = sliced.rpm, tps = sliced.tps;
  const runStart = findRunStart(sliced, localWotStart, localWotEnd, rateHz);
  const segments = [];

  if (localWotStart > 0) {
    segments.push({ phase: "Lead-in", startIdx: 0, endIdx: localWotStart - 1 });
  }
  if (runStart.idx > localWotStart) {
    segments.push({ phase: runStart.source.includes("two-step") || runStart.source === "trans brake release" || runStart.source === "boost timer start"
      ? "Two-Step Hold" : "Throttle Application", startIdx: localWotStart, endIdx: runStart.idx - 1 });
  }

  const shifts = detectShifts(sliced, runStart.idx, localWotEnd, rateHz, settings);
  let segStart = runStart.idx, gearNum = 1;
  for (const sh of shifts) {
    segments.push({ phase: `Gear ${gearNum}`, startIdx: segStart, endIdx: sh.peakIdx, shiftRpm: sh.rpmBefore });
    segStart = sh.dropIdx;
    gearNum++;
  }
  segments.push({ phase: shifts.length ? `Gear ${gearNum}` : "Wide Open Throttle", startIdx: segStart, endIdx: localWotEnd });

  if (localWotEnd < totalRows - 1) {
    segments.push({ phase: "Throttle Lift / Cool-down", startIdx: localWotEnd + 1, endIdx: totalRows - 1 });
  }

  const confidence = runStart.source === "throttle onset (no launch marker found)" ? "low" : "medium";
  return { segments, runStart, shifts, confidence };
}


// Closed loop compensation, evaluated only over "stabilized" WOT (after the
// launch/tip-in transient dies down) so a brief tip-in spike doesn't dominate
// the read on how hard closed loop is working during the meat of the pull.
// Sign convention (positive = adding fuel) has NOT been validated against
// real data (see analysis notes) - only magnitude is reported, direction is
// deliberately left unclaimed.
// Rough BSFC (fuel burned per horsepower per hour) defaults by fuel type.
// These are industry rule-of-thumb ranges, not measured for any specific
// engine - actual BSFC varies with engine efficiency, boost level, and tune
// quality, so the resulting horsepower number is a trend/ballpark estimate,
// not a dyno-equivalent figure.
const BSFC_DEFAULTS = {
  gasoline_na: 0.50,
  gasoline_boosted: 0.60,
  e85: 0.80,
  e98: 0.85,
  methanol: 1.10,
};

// Fuel type inference from measured AFR under load. Uses the richest
// sustained (5th-percentile) measured AFR during WOT as the primary signal,
// since that reflects real exhaust chemistry regardless of what
// stoichiometric reference the ECU's target-AFR display is configured to
// use. A tune can be logged on a gasoline-relative AFR scale even when
// running an alcohol fuel - flagged explicitly when that pattern shows up.
function detectFuelType(sliced, wotIdx) {
  if (channelStatus(sliced.afr_avg, { plausibleMax: 3 }) !== "available") return { available: false };
  const afrVals = wotIdx.map((i) => sliced.afr_avg[i]).filter((v) => !Number.isNaN(v));
  if (afrVals.length < 10) return { available: false };
  const sorted = [...afrVals].sort((a, b) => a - b);
  const richAfr = sorted[Math.max(0, Math.floor(sorted.length * 0.05))];

  const targetVals = wotIdx.map((i) => sliced.target_afr[i]).filter((v) => !Number.isNaN(v));
  const targetAvailable = targetVals.length >= 10 && channelStatus(sliced.target_afr, { plausibleMax: 3 }) === "available";
  const targetMean = targetAvailable ? nanmean(targetVals) : null;
  const targetLooksGasScale = targetAvailable && targetMean > 10.5;

  let guess, confidence, note;
  if (richAfr < 7.2) {
    guess = "Methanol"; confidence = "medium";
    note = `Richest sustained AFR reached ${richAfr.toFixed(1)}, consistent with methanol (stoich ~6.4:1).`;
  } else if (richAfr < 8.6) {
    guess = "E98 or methanol"; confidence = "low";
    note = `Richest sustained AFR reached ${richAfr.toFixed(1)}, in the range where E98 and methanol overlap, can't distinguish those two from AFR alone.`;
  } else if (richAfr < 10.3) {
    guess = "E85 or E98"; confidence = "low";
    note = `Richest sustained AFR reached ${richAfr.toFixed(1)}, consistent with an ethanol fuel (E85 stoich ~9.8:1, E98 ~9.0:1), can't tell the exact blend from AFR alone.`;
  } else if (richAfr < 11.2) {
    guess = "Ambiguous"; confidence = "low";
    note = `Richest sustained AFR reached ${richAfr.toFixed(1)}, richer than typical gasoline tuning but not clearly ethanol either.` +
      (targetLooksGasScale ? ` Target AFR is averaging ${targetMean.toFixed(1)}, a gasoline-shaped number, if this car actually runs E98, it may be logged on a gasoline AFR scale rather than ethanol's.` : "");
  } else {
    guess = "Gasoline"; confidence = targetLooksGasScale ? "medium" : "low";
    note = `Richest sustained AFR reached ${richAfr.toFixed(1)}, consistent with gasoline (stoich 14.7:1).`;
  }
  return { available: true, richAfr, guess, confidence, note, targetLooksGasScale, targetMean };
}

function resolveBsfc(settings, fuelGuess) {
  if (settings.bsfcOverride) return settings.bsfcOverride;
  if (settings.fuelType && settings.fuelType !== "auto") return BSFC_DEFAULTS[settings.fuelType] || BSFC_DEFAULTS.gasoline_boosted;
  if (fuelGuess && fuelGuess.available) {
    if (fuelGuess.guess === "Methanol") return BSFC_DEFAULTS.methanol;
    if (fuelGuess.guess.includes("E98")) return BSFC_DEFAULTS.e98;
    if (fuelGuess.guess.includes("E85")) return BSFC_DEFAULTS.e85;
  }
  return BSFC_DEFAULTS.gasoline_boosted;
}

// Fuel-flow-based horsepower/torque estimate - a rough substitute for a real
// dyno pull, not a replacement for one. BSFC varies with engine efficiency,
// tune quality, and boost level, so treat the output as a trend/ballpark
// figure. Torque comes from the standard HP = torque x RPM / 5252
// relationship, which is exact regardless of engine.
function computeHpTorqueCurve(sliced, wotIdx, bsfc) {
  if (channelStatus(sliced.fuel_flow, { plausibleMax: 5 }) !== "available") return { available: false };
  const points = wotIdx
    .map((i) => ({ rpm: sliced.rpm[i], ff: sliced.fuel_flow[i] }))
    .filter((p) => !Number.isNaN(p.rpm) && !Number.isNaN(p.ff) && p.rpm > 500);
  if (points.length < 10) return { available: false };

  const bins = {};
  for (const p of points) {
    const b = Math.round(p.rpm / 100) * 100;
    (bins[b] ??= []).push(p.ff);
  }
  const curve = Object.entries(bins)
    .filter(([, ffs]) => ffs.length >= 2)
    .map(([rpmKey, ffs]) => {
      const ff = nanmedian(ffs);
      const hp = ff / bsfc;
      const rpmNum = Number(rpmKey);
      return { rpm: rpmNum, hp, tq: (hp * 5252) / rpmNum };
    })
    .sort((a, b) => a.rpm - b.rpm);

  if (!curve.length) return { available: false };
  const peakHp = curve.reduce((a, b) => (b.hp > a.hp ? b : a));
  const peakTq = curve.reduce((a, b) => (b.tq > a.tq ? b : a));
  return { available: true, curve, peakHp, peakTq, bsfc };
}

function computeClComp(sliced, win, rateHz, settings = DEFAULT_SETTINGS) {
  const localWotStart = win.wotStart - win.start;
  const localWotEnd = win.wotEnd - win.start;
  const cl = sliced.cl_comp;
  if (!cl || channelStatus(cl, { minFinitePct: 0.5 }) !== "available") {
    return { available: false, reason: "missing" };
  }
  const runStart = findRunStart(sliced, localWotStart, localWotEnd, rateHz);
  const stableStart = Math.min(localWotEnd, runStart.idx + secondsToSamples(settings.clCompStableDelay, rateHz));
  const stableEnd = localWotEnd;
  if (stableEnd - stableStart < 5) return { available: false, reason: "too-short" };

  const idxs = [];
  for (let i = stableStart; i <= stableEnd; i++) if (!Number.isNaN(cl[i])) idxs.push(i);
  const seg = idxs.map(i => cl[i]);
  if (seg.length < 5) return { available: false, reason: "insufficient-samples" };

  // A genuinely inactive/disabled closed loop sits at a hard, unmoving 0;
  // real correction activity, even small, has some wobble to it.
  const activeLikely = nanstd(seg) > 0.1 || seg.some(v => Math.abs(v) > 0.5);

  const absVals = seg.map(Math.abs);
  const sortedAbs = [...absVals].sort((a, b) => a - b);
  const p95 = sortedAbs[Math.min(sortedAbs.length - 1, Math.floor(sortedAbs.length * 0.95))];
  const timeAbove = (thresh) => absVals.filter(v => v >= thresh).length / rateHz;

  let afrErrMean = null;
  if (channelStatus(sliced.afr_avg, { plausibleMax: 3 }) === "available" && channelStatus(sliced.target_afr, { plausibleMax: 3 }) === "available") {
    const errs = idxs.map(i => sliced.afr_avg[i] - sliced.target_afr[i]).filter(v => !Number.isNaN(v));
    if (errs.length) afrErrMean = nanmean(errs);
  }

  return {
    available: true, activeLikely,
    meanSigned: nanmean(seg), medianSigned: nanmedian(seg),
    meanAbs: nanmean(absVals), medianAbs: nanmedian(absVals),
    peakPos: Math.max(...seg, 0), peakNeg: Math.min(...seg, 0), p95,
    secAboveMonitor: timeAbove(settings.clCompMonitor),
    secAboveWarning: timeAbove(settings.clCompWarning),
    secAboveCritical: timeAbove(settings.clCompCritical),
    windowSeconds: seg.length / rateHz, afrErrMean,
  };
}

// Extracts a compact, storable summary of one analyzed pass - the numbers
// worth tracking across a session, not the full log. Used for between-rounds
// session tracking (browser localStorage), not for the single-log report.
function buildSessionSummary(result, filename) {
  const { flags, channels, metrics, window, segmentation } = result;
  if (!channels || !window) return null;
  const wotIdx = [];
  const localWotStart = window.wotStart - window.start, localWotEnd = window.wotEnd - window.start;
  for (let i = localWotStart; i <= localWotEnd; i++) if (channels.tps[i] > 85) wotIdx.push(i);
  // Oil minimum and lean fraction are tracked over the pass itself (launch
  // and shifts excluded), matching what the checks evaluate.
  const passIdx = window.passIdx && window.passIdx.length >= 10 ? window.passIdx : wotIdx;

  const pick = (arr, idxs) => idxs.map((i) => arr[i]);
  const peakBoost = nanmax(pick(channels.boost_psig, wotIdx));
  const oilMin = channelStatus(channels.oil_pressure, { plausibleMax: 5 }) === "available"
    ? nanmin(pick(channels.oil_pressure, passIdx)) : null;

  let leanFrac = null;
  if (channelStatus(channels.afr_avg, { plausibleMax: 3 }) === "available" && channelStatus(channels.target_afr, { plausibleMax: 3 }) === "available") {
    const errs = passIdx.map((i) => channels.afr_avg[i] - channels.target_afr[i]);
    leanFrac = errs.length ? errs.filter((e) => e > 0.3).length / errs.length : null;
  }

  const critical = flags.filter((f) => f.sev === "critical").length;
  const warn = flags.filter((f) => f.sev === "warn").length;

  return {
    timestamp: Date.now(),
    filename,
    peakBoost: Number.isFinite(peakBoost) ? peakBoost : null,
    spoolSeconds: metrics.spool ? metrics.spool.seconds : null,
    spoolPlateau: metrics.spool ? metrics.spool.plateauLevel : null,
    leanFrac,
    oilMin: Number.isFinite(oilMin) ? oilMin : null,
    clCompP95: metrics.clComp && metrics.clComp.available ? metrics.clComp.p95 : null,
    dutyPeak: metrics.duty ? metrics.duty.max : null,
    peakHp: metrics.powerCurve && metrics.powerCurve.available ? metrics.powerCurve.peakHp.hp : null,
    shiftRpms: segmentation && segmentation.shifts ? segmentation.shifts.map((s) => Math.round(s.rpmBefore)) : [],
    critical, warn,
  };
}

function computeMetrics(sliced, win, settings = DEFAULT_SETTINGS) {
  if (!win) return null;
  const localWotStart = win.wotStart - win.start;
  const localWotEnd = win.wotEnd - win.start;
  let wotIdx = [];
  for (let i = localWotStart; i <= localWotEnd; i++) if (sliced.tps[i] > 85) wotIdx.push(i);
  // Fuel type and the power estimate should come from the pass itself, not
  // the two-step hold (where fuel is often a fixed launch value).
  if (win.passIdx && win.passIdx.length >= 10) wotIdx = win.passIdx;
  const fuelType = detectFuelType(sliced, wotIdx);
  const bsfc = resolveBsfc(settings, fuelType);
  return {
    spool: computeSpoolTime(sliced, localWotStart, localWotEnd, win.rateHz),
    fade: computeBoostFade(sliced, localWotStart, localWotEnd),
    afrByBand: computeAfrByBand(sliced, localWotStart, localWotEnd, win.rateHz),
    duty: computeDutyCeiling(sliced, localWotStart, localWotEnd, settings),
    runStart: findRunStart(sliced, localWotStart, localWotEnd, win.rateHz),
    clComp: computeClComp(sliced, win, win.rateHz, settings),
    fuelType,
    powerCurve: computeHpTorqueCurve(sliced, wotIdx, bsfc),
  };
}


function analyzeWOT(channels, nRows, padSeconds = 5, settings = DEFAULT_SETTINGS) {
  const win = findWOTWindow(channels, nRows, padSeconds);
  if (!win) return { flags: [{ name: "Wide Open Throttle", sev: "info", channel: null, idxs: [],
    msg: "No wide-open-throttle segment found in this log." }], window: null };
  const sliced = sliceChannels(channels, win.start, win.end);
  const coreRange = [win.wotStart - win.start, win.wotEnd - win.start];
  // Segment first (launch, shifts) so the safety checks can be run on the
  // pass itself rather than the two-step hold and gear changes.
  const segmentation = segmentRun(sliced, win, win.rateHz, settings);
  const pass = buildPassIdx(sliced, win, segmentation, win.rateHz);
  win.passIdx = pass.idxs;
  const flags = analyze(sliced, win.end - win.start + 1, win.rateHz, coreRange, settings, pass.idxs);
  const metrics = computeMetrics(sliced, win, settings);
  appendMetricFlags(flags, metrics, sliced, win, settings);
  return { flags, window: win, channels: sliced, metrics, segmentation };
}

function appendMetricFlags(flags, metrics, sliced, win, settings = DEFAULT_SETTINGS) {
  if (!metrics) return;

  if (metrics.spool) {
    // If the log file itself begins while the car is already sitting on the
    // two-step at WOT, the true start of the hold wasn't recorded, so the
    // measured time is only a lower bound.
    const truncated = win.start === 0 && metrics.spool.startIdx === 0 && sliced.tps[0] > 85;
    const origin = (metrics.spool.fromTwoStep ? "the two-step" : "throttle onset (no two-step hold detected)") +
      (truncated ? ". The log starts partway through the hold, so the real spool time is longer than this, start logging before staging for an exact number" : "");
    if (metrics.spool.reached && !metrics.spool.viaStabilize) {
      flags.push({ name: "Spool Time", sev: "info", channel: "boost_psig", idxs: [],
        msg: `${metrics.spool.seconds.toFixed(2)}s to reach 90% of ${metrics.spool.goal.toFixed(0)} psi target during the hold, timed from ${origin}.` });
    } else if (metrics.spool.reached && metrics.spool.viaStabilize) {
      flags.push({ name: "Spool Time", sev: "info", channel: "boost_psig", idxs: [],
        msg: `${metrics.spool.seconds.toFixed(2)}s for boost and RPM to stabilize on the two-step (settled around ${metrics.spool.plateauLevel.toFixed(0)} psi), timed from ${origin}.` });
    } else {
      flags.push({ name: "Spool Time", sev: "warn", channel: "boost_psig", idxs: [],
        msg: `Boost and RPM never stabilized before release, timed from ${origin}.` });
    }
  }

  if (metrics.fade) {
    const f = metrics.fade;
    if (f.fadePsi < -3) {
      flags.push({ name: "Boost Fade", sev: "warn", channel: "boost_psig", idxs: [],
        msg: `Boost fell off ${Math.abs(f.fadePsi).toFixed(1)} psi from the first third of the pull to the last third (heat soak or a tiring wastegate spring are common causes).` });
    } else {
      flags.push({ name: "Boost Fade", sev: "ok", channel: "boost_psig", idxs: [],
        msg: `Boost held steady through the pull, ${f.fadePsi >= 0 ? "+" : ""}${f.fadePsi.toFixed(1)} psi first-third to last-third.` });
    }
  }

  if (metrics.duty) {
    if (metrics.duty.pinnedFrac > 0.1) {
      flags.push({ name: "Injector Duty Cycle", sev: "critical", channel: "duty_cycle", idxs: [],
        msg: `Duty cycle pinned above ${settings.dutyCycleCritical}% for ${Math.round(metrics.duty.pinnedFrac * 100)}% of the pull (peak ${metrics.duty.max.toFixed(0)}%), the injectors are maxed out regardless of what's being commanded.` });
    } else if (metrics.duty.max >= settings.dutyCycleWarn) {
      flags.push({ name: "Injector Duty Cycle", sev: "warn", channel: "duty_cycle", idxs: [],
        msg: `Peak duty cycle ${metrics.duty.max.toFixed(0)}%, within the ${settings.dutyCycleWarn}-${settings.dutyCycleCritical}% range worth keeping an eye on.` });
    } else {
      flags.push({ name: "Injector Duty Cycle", sev: "ok", channel: "duty_cycle", idxs: [],
        msg: `Peak duty cycle ${metrics.duty.max.toFixed(0)}%, headroom left.` });
    }
  }

  // Closed Loop Compensation. Sign convention (whether positive means adding
  // or removing fuel) hasn't been validated against real data, so this only
  // ever reports magnitude, never a fuel-direction claim.
  const cl = metrics.clComp;
  if (!cl || !cl.available) {
    flags.push({ name: "Closed Loop Compensation", sev: "info", channel: "cl_comp", idxs: [],
      msg: "Closed Loop Compensation was not available in this log. Exporting the log as CSV with the CL Comp channel included may allow this check." });
  } else if (!cl.activeLikely) {
    flags.push({ name: "Closed Loop Compensation", sev: "info", channel: "cl_comp", idxs: [],
      msg: "CL Comp sat flat near zero through stabilized wide-open-throttle. Could mean closed loop is disabled or inactive here, or that compensation is genuinely zero, the log alone can't tell which, check not evaluated." });
  } else {
    const p95 = cl.p95;
    const afrNote = cl.afrErrMean !== null
      ? ` AFR averaged ${cl.afrErrMean >= 0 ? "+" : ""}${cl.afrErrMean.toFixed(2)} from target over the same window.`
      : "";
    if (p95 >= settings.clCompCritical && cl.secAboveCritical >= settings.clCompMinDuration) {
      const afrStillOff = cl.afrErrMean !== null && Math.abs(cl.afrErrMean) > 0.3;
      flags.push({ name: "Closed Loop Compensation", sev: afrStillOff ? "critical" : "warn", channel: "cl_comp", idxs: [],
        msg: `Closed loop correction reached ${p95.toFixed(0)}% (95th percentile) through stabilized wide-open-throttle, sustained for ${cl.secAboveCritical.toFixed(2)}s above ${settings.clCompCritical}%.` + afrNote +
          (afrStillOff
            ? " AFR is still off target even with that much correction applied, closed loop may be running out of authority."
            : " AFR is tracking target, but closed loop is doing significant corrective work to get there, worth reviewing the base fuel table, injector data, and fuel pressure rather than trusting the AFR result alone.") });
    } else if (p95 >= settings.clCompWarning && cl.secAboveWarning >= settings.clCompMinDuration) {
      flags.push({ name: "Closed Loop Compensation", sev: "warn", channel: "cl_comp", idxs: [],
        msg: `Closed loop correction reached ${p95.toFixed(0)}% (95th percentile) through stabilized wide-open-throttle, sustained for ${cl.secAboveWarning.toFixed(2)}s above ${settings.clCompWarning}%.` + afrNote });
    } else if (p95 >= settings.clCompMonitor) {
      flags.push({ name: "Closed Loop Compensation", sev: "info", channel: "cl_comp", idxs: [],
        msg: `Closed loop correction peaked around ${p95.toFixed(0)}% through stabilized wide-open-throttle, within a normal monitoring range.` + afrNote });
    } else {
      flags.push({ name: "Closed Loop Compensation", sev: "ok", channel: "cl_comp", idxs: [],
        msg: `Closed loop correction stayed within ±${settings.clCompMonitor}% through stabilized wide-open-throttle. Base fuel model looks reasonably close to target here.` });
    }
  }
}

// ============================================================
// UI wiring
// ============================================================
const dropzone = document.getElementById('dropzone');
const fileInput = document.getElementById('fileInput');
const filenameChip = document.getElementById('filenameChip');
const processing = document.getElementById('processing');
const results = document.getElementById('results');
const errorBox = document.getElementById('errorBox');
const errorMsg = document.getElementById('errorMsg');
const summaryLine = document.getElementById('summaryLine');
const checkList = document.getElementById('checkList');
const clearList = document.getElementById('clearList');
const clearItems = document.getElementById('clearItems');
const resetLink = document.getElementById('resetLink');
const compareLink = document.getElementById('compareLink');
const compareZone = document.getElementById('compareZone');
const compareResults = document.getElementById('compareResults');
const dropzoneB = document.getElementById('dropzoneB');
const fileInputB = document.getElementById('fileInputB');
const filenameChipB = document.getElementById('filenameChipB');
const settingsLink = document.getElementById('settingsLink');
const settingsPanel = document.getElementById('settingsPanel');
const settingsSave = document.getElementById('settingsSave');
const settingsReset = document.getElementById('settingsReset');

const SETTINGS_KEY = 'bps-log-analyzer-settings';
const SETTINGS_FIELDS = {
  oilPressureRule: 'setOilRule', oilPressureRatio: 'setOilRatio', oilPressureFlatMin: 'setOilFlat',
  boostToleranceP: 'setBoostTol', afrLeanTolerance: 'setAfrTol',
  coolantTempLimit: 'setCts', intakeTempLimit: 'setMat',
  dutyCycleWarn: 'setDutyWarn', dutyCycleCritical: 'setDutyCrit',
};

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const saved = JSON.parse(raw);
    return { ...DEFAULT_SETTINGS, ...saved };
  } catch (e) {
    return { ...DEFAULT_SETTINGS };
  }
}

function populateSettingsForm(settings) {
  for (const [key, id] of Object.entries(SETTINGS_FIELDS)) {
    const el = document.getElementById(id);
    if (el) el.value = settings[key];
  }
}

function readSettingsForm() {
  const out = { ...currentSettings };
  for (const [key, id] of Object.entries(SETTINGS_FIELDS)) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (el.tagName === 'SELECT') out[key] = el.value;
    else {
      const v = parseFloat(el.value);
      if (!Number.isNaN(v)) out[key] = v;
    }
  }
  return out;
}

let currentSettings = loadSettings();

const SESSION_KEY = 'bps-log-analyzer-session';
const SESSION_GAP_MS = 4 * 60 * 60 * 1000; // suggest a new session after a 4h gap, never auto-resets

function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return { startedAt: Date.now(), passes: [] };
    const parsed = JSON.parse(raw);
    return { startedAt: parsed.startedAt || Date.now(), passes: parsed.passes || [] };
  } catch (e) {
    return { startedAt: Date.now(), passes: [] };
  }
}
function saveSession(session) {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch (e) {}
}

let currentSession = loadSession();
let sessionResultsCache = {}; // in-memory only: id -> {name, result}, cleared on reload

function addPassToSession(result, filename) {
  const summary = buildSessionSummary(result, filename);
  if (!summary) return;
  summary.id = Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  currentSession.passes.push(summary);
  sessionResultsCache[summary.id] = { name: filename, result };
  saveSession(currentSession);
  renderSession();
}

const SESSION_METRICS = [
  { key: 'peakBoost', label: 'Boost', fmt: v => v.toFixed(1) + ' psi', watch: 'down' },
  { key: 'spoolSeconds', label: 'Spool', fmt: v => v.toFixed(2) + 's', watch: 'up' },
  { key: 'leanFrac', label: 'Lean', fmt: v => Math.round(v * 100) + '%', watch: 'up' },
  { key: 'oilMin', label: 'Oil Min', fmt: v => v.toFixed(0) + ' psi', watch: 'down' },
  { key: 'clCompP95', label: 'CL Comp', fmt: v => v.toFixed(0) + '%', watch: 'up' },
  { key: 'dutyPeak', label: 'Duty', fmt: v => v.toFixed(0) + '%', watch: 'up' },
  { key: 'peakHp', label: 'Est HP', fmt: v => Math.round(v).toLocaleString(), watch: null },
];

function trendArrow(curr, prev, watchDir) {
  if (curr == null || prev == null || prev === 0) return '';
  const pctChange = (curr - prev) / Math.abs(prev);
  if (Math.abs(pctChange) < 0.02) return '<span>&rarr;</span>';
  const dir = pctChange > 0 ? 'up' : 'down';
  const arrow = dir === 'up' ? '&uarr;' : '&darr;';
  const isWatchDir = watchDir && dir === watchDir;
  const mag = Math.abs(pctChange);
  let cls = '';
  if (isWatchDir && mag > 0.25) cls = 'trend-alert';
  else if (isWatchDir && mag > 0.10) cls = 'trend-watch';
  return `<span class="${cls}">${arrow}</span>`;
}

function renderSession() {
  const section = document.getElementById('sessionSection');
  const gapNoteEl = document.getElementById('sessionGapNote');
  const tableEl = document.getElementById('sessionTable');
  const passes = currentSession.passes;
  if (!passes.length) { section.style.display = 'none'; return; }
  section.style.display = 'block';

  const last = passes[passes.length - 1];
  const gapMs = passes.length > 1 ? last.timestamp - passes[passes.length - 2].timestamp : 0;
  if (passes.length > 1 && gapMs > SESSION_GAP_MS) {
    gapNoteEl.innerHTML = `<div class="session-gap-note">It's been ${(gapMs / 3600000).toFixed(1)} hours since the last pass in this session. If this is a new session (different day/event), click "start new session" below.</div>`;
  } else {
    gapNoteEl.innerHTML = '';
  }

  let verdictHtml = '';
  if (last.critical > 0) {
    verdictHtml = `<div class="session-verdict review">Review before staging again - ${last.critical} critical finding${last.critical === 1 ? '' : 's'} on the last pass</div>`;
  } else {
    verdictHtml = `<div class="session-verdict go">Looks clear for another pass</div>`;
  }

  const headerCells = SESSION_METRICS.map(m => `<th>${m.label}</th>`).join('');
  const rows = passes.map((p, idx) => {
    const prev = idx > 0 ? passes[idx - 1] : null;
    const cells = SESSION_METRICS.map(m => {
      const v = p[m.key];
      if (v === null || v === undefined) return '<td>&mdash;</td>';
      const trend = prev ? trendArrow(v, prev[m.key], m.watch) : '';
      return `<td>${m.fmt(v)} ${trend}</td>`;
    }).join('');
    const shiftStr = p.shiftRpms && p.shiftRpms.length ? p.shiftRpms.join(', ') : '&mdash;';
    const time = new Date(p.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const cbId = p.id || '';
    return `<tr><td><input type="checkbox" class="session-compare-cb" data-id="${cbId}"></td><td><b>${idx + 1}</b></td><td>${time}</td>${cells}<td>${shiftStr}</td><td>${p.critical}c/${p.warn}w</td></tr>`;
  }).join('');

  tableEl.innerHTML = verdictHtml + `<table class="session-table">
    <tr><th></th><th>Pass</th><th>Time</th>${headerCells}<th>Shifts</th><th>Flags</th></tr>
    ${rows}
  </table>
  <div style="margin-top:10px;">
    <a class="reset-link" id="sessionCompareLink" style="opacity:.4;pointer-events:none;">compare selected passes</a>
  </div>
  <div id="sessionCompareNote" class="power-caveat"></div>`;

  const checkboxes = Array.from(tableEl.querySelectorAll('.session-compare-cb'));
  const compareLink = document.getElementById('sessionCompareLink');
  const compareNote = document.getElementById('sessionCompareNote');
  checkboxes.forEach(cb => cb.addEventListener('change', () => {
    const checked = checkboxes.filter(c => c.checked);
    if (checked.length > 2) { cb.checked = false; return; }
    if (checked.length === 2) {
      compareLink.style.opacity = '1';
      compareLink.style.pointerEvents = 'auto';
    } else {
      compareLink.style.opacity = '.4';
      compareLink.style.pointerEvents = 'none';
    }
    compareNote.textContent = '';
  }));
  compareLink.addEventListener('click', () => {
    const checked = checkboxes.filter(c => c.checked);
    if (checked.length !== 2) return;
    const [idA, idB] = checked.map(c => c.dataset.id);
    const cacheA = sessionResultsCache[idA], cacheB = sessionResultsCache[idB];
    if (!cacheA || !cacheB) {
      compareNote.textContent = 'One or both of those passes were analyzed before this page was last reloaded, the full log data for those only lives in memory for this browser tab. Re-upload that log to compare it directly.';
      return;
    }
    logA = cacheA;
    renderComparison(cacheA, cacheB);
  });
}

document.getElementById('sessionResetLink').addEventListener('click', () => {
  currentSession = { startedAt: Date.now(), passes: [] };
  sessionResultsCache = {};
  saveSession(currentSession);
  renderSession();
});
renderSession();

populateSettingsForm(currentSettings);

settingsLink.addEventListener('click', () => {
  settingsPanel.style.display = settingsPanel.style.display === 'none' ? 'block' : 'none';
});
settingsSave.addEventListener('click', () => {
  currentSettings = readSettingsForm();
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(currentSettings)); } catch (e) {}
  settingsLink.textContent = 'analysis settings (saved)';
  setTimeout(() => { settingsLink.textContent = 'analysis settings'; }, 1500);
});
settingsReset.addEventListener('click', () => {
  currentSettings = { ...DEFAULT_SETTINGS };
  populateSettingsForm(currentSettings);
  try { localStorage.removeItem(SETTINGS_KEY); } catch (e) {}
});

let logA = null; // { name, result } populated once the first log finishes analyzing

dropzone.addEventListener('click', () => fileInput.click());
dropzone.addEventListener('dragover', e => { e.preventDefault(); dropzone.classList.add('drag'); });
dropzone.addEventListener('dragleave', () => dropzone.classList.remove('drag'));
dropzone.addEventListener('drop', e => {
  e.preventDefault(); dropzone.classList.remove('drag');
  if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
});
fileInput.addEventListener('change', e => { if (e.target.files.length) handleFile(e.target.files[0]); });
resetLink.addEventListener('click', () => location.reload());
compareLink.addEventListener('click', () => {
  compareZone.style.display = 'block';
  compareLink.style.display = 'none';
});
dropzoneB.addEventListener('click', () => fileInputB.click());
dropzoneB.addEventListener('dragover', e => { e.preventDefault(); dropzoneB.classList.add('drag'); });
dropzoneB.addEventListener('dragleave', () => dropzoneB.classList.remove('drag'));
dropzoneB.addEventListener('drop', e => {
  e.preventDefault(); dropzoneB.classList.remove('drag');
  if (e.dataTransfer.files.length) handleFileB(e.dataTransfer.files[0]);
});
fileInputB.addEventListener('change', e => { if (e.target.files.length) handleFileB(e.target.files[0]); });

function parseFile(file) {
  return new Promise((resolve, reject) => {
    const isCSV = file.name.toLowerCase().endsWith('.csv');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let parsed;
        if (isCSV) {
          parsed = parseCSV(e.target.result);
        } else {
          try {
            parsed = parseDL(e.target.result);
          } catch (err) {
            if (err.code === "UNSUPPORTED_DL_FORMAT") {
              throw new Error("This .dl file isn't in the supported Holley V6 format. Export a CSV from your Holley EFI software instead (File -> Export -> name it with a .csv extension) and drop that in here.");
            }
            throw err;
          }
        }
        resolve(parsed);
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error("Couldn't read that file from disk. Try again."));
    if (isCSV) reader.readAsText(file); else reader.readAsArrayBuffer(file);
  });
}

function handleFile(file) {
  errorBox.classList.remove('active');
  results.classList.remove('active');
  compareResults.innerHTML = '';
  compareZone.style.display = 'none';
  compareLink.style.display = 'inline-block';
  filenameChip.innerHTML = `<div class="filename-chip">${file.name}</div>`;
  processing.classList.add('active');
  parseFile(file).then(parsed => {
    setTimeout(() => {
      const result = analyzeWOT(parsed.channels, parsed.nRows, 5, currentSettings);
      logA = { name: file.name, result };
      addPassToSession(result, file.name);
      renderResults(result, parsed.nRows);
    }, 350); // let the sweep animation read as "working"
  }).catch(err => {
    processing.classList.remove('active');
    errorMsg.textContent = err.message || "Something about this file didn't parse. Try exporting a fresh CSV from Holley EFI software.";
    errorBox.classList.add('active');
  });
}

function handleFileB(file) {
  filenameChipB.innerHTML = `<div class="filename-chip">${file.name}</div>`;
  parseFile(file).then(parsed => {
    const result = analyzeWOT(parsed.channels, parsed.nRows, 5, currentSettings);
    const logB = { name: file.name, result };
    renderComparison(logA, logB);
  }).catch(err => {
    filenameChipB.innerHTML = `<div class="filename-chip" style="background:var(--critical);">${err.message}</div>`;
  });
}

function sparklineSVG(arr, idxs, sev) {
  const n = arr.length;
  const maxPts = 300;
  const step = Math.max(1, Math.floor(n / maxPts));
  const pts = [];
  for (let i = 0; i < n; i += step) if (!Number.isNaN(arr[i])) pts.push([i, arr[i]]);
  if (!pts.length) return '';
  const ys = pts.map(p => p[1]);
  const lo = Math.min(...ys), hi = Math.max(...ys);
  const range = hi - lo || 1;
  const W = 600, H = 46, pad = 3;
  const toXY = ([i, v]) => [ (i/(n-1))*W, H-pad - ((v-lo)/range)*(H-2*pad) ];
  const line = pts.map(toXY).map(p => p.join(',')).join(' ');
  const flagSet = new Set(idxs);
  const markers = idxs.length && idxs.length < 400
    ? idxs.map(i => { const [x,y] = toXY([i, arr[i]]); return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="1.6" fill="var(--${sev})"/>`; }).join('')
    : '';
  const color = sev === 'ok' ? 'var(--ink-muted)' : `var(--${sev})`;
  return `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">
    <polyline points="${line}" fill="none" stroke="${color}" stroke-width="1.4"/>
    ${markers}
  </svg>`;
}

function fmtSec(v) { return v === null || v === undefined || Number.isNaN(v) ? ' - ' : v.toFixed(2) + 's'; }
function fmtPsi(v) { return v === null || v === undefined || Number.isNaN(v) ? ' - ' : (v>=0?'+':'') + v.toFixed(1) + ' psi'; }

function renderShiftAnalysis(segmentation, window) {
  const summaryEl = document.getElementById('shiftSummary');
  const tableEl = document.getElementById('shiftTable');
  if (!segmentation || !window) { summaryEl.innerHTML = ''; tableEl.innerHTML = ''; return; }
  const shifts = segmentation.shifts;
  if (!shifts.length) {
    summaryEl.innerHTML = `<div class="shift-summary-line">No confirmed shifts detected. This may be a single-gear pull, a log without sufficient shift data, or a pass where shift events could not be identified reliably. (Shift detection is tuned conservatively and hasn't yet been checked against a log with a confirmed real shift.)</div>`;
    tableEl.innerHTML = '';
    return;
  }
  const rpms = shifts.map(s => s.rpmBefore);
  const avgRpm = rpms.reduce((a,b)=>a+b,0) / rpms.length;
  const spread = Math.max(...rpms) - Math.min(...rpms);
  const fastest = shifts.reduce((a,b)=> b.durationSec < a.durationSec ? b : a);
  const slowest = shifts.reduce((a,b)=> b.durationSec > a.durationSec ? b : a);
  const biggestDrop = shifts.reduce((a,b)=> b.rpmDrop > a.rpmDrop ? b : a);
  const boostLosses = shifts.filter(s => s.boostLoss !== null);
  const biggestBoostLoss = boostLosses.length ? boostLosses.reduce((a,b)=> b.boostLoss > a.boostLoss ? b : a) : null;

  summaryEl.innerHTML = `<div class="shift-summary-line">
    ${shifts.length} shift${shifts.length===1?'':'s'} detected (inferred gear numbers, not read from a gear-position channel).
    Average shift RPM ${avgRpm.toFixed(0)}, spread ${spread.toFixed(0)} rpm.
    Fastest shift ${fmtSec(fastest.durationSec)}, slowest ${fmtSec(slowest.durationSec)}.
    Largest RPM drop ${biggestDrop.rpmDrop.toFixed(0)} rpm (${biggestDrop.rpmDropPct.toFixed(0)}%).
    ${biggestBoostLoss ? `Largest boost loss ${biggestBoostLoss.boostLoss.toFixed(1)} psi.` : ''}
  </div>`;

  const rows = shifts.map((s, idx) => `
    <tr>
      <td>${idx+1}-${idx+2}</td>
      <td>${s.rpmBefore.toFixed(0)}</td>
      <td>${s.rpmAfter.toFixed(0)}</td>
      <td>${s.rpmDrop.toFixed(0)} (${s.rpmDropPct.toFixed(0)}%)</td>
      <td>${fmtSec(s.durationSec)}</td>
      <td>${s.boostLoss !== null ? s.boostLoss.toFixed(1) + ' psi' : ' - '}</td>
      <td>${fmtSec(s.boostRecoverSec)}</td>
      <td>${s.confidence}${s.lifted ? ' (lift)' : ''}</td>
    </tr>`).join('');

  tableEl.innerHTML = `
    <table class="shift-table">
      <tr><th>Shift</th><th>Shift RPM</th><th>Post-shift</th><th>RPM drop</th><th>Duration</th><th>Boost loss</th><th>Recovery</th><th>Confidence</th></tr>
      ${rows}
    </table>
    ${shifts.some(s => s.limiterHint) ? `<div class="shift-note">${shifts.filter(s=>s.limiterHint).map((s,i)=>`Shift ${i+1}: ${s.limiterHint}`).join(' ')}</div>` : ''}
    <div class="shift-note">Only no-lift and lift shifts with a clean recovery are counted, this can't yet tell a real shift apart from tire spin, converter coupling, or traction-control intervention without a speed or gear channel, treat as a lead worth checking, not a certainty.</div>
  `;
}

function powerCurveSVG(curve) {
  const W = 600, H = 200, padL = 34, padR = 10, padT = 10, padB = 24;
  const rpmLo = curve[0].rpm, rpmHi = curve[curve.length - 1].rpm;
  const rpmRange = (rpmHi - rpmLo) || 1;
  const allVals = curve.flatMap(p => [p.hp, p.tq]);
  const vLo = 0, vHi = Math.max(...allVals) * 1.08;
  const toXY = (rpm, v) => [
    padL + ((rpm - rpmLo) / rpmRange) * (W - padL - padR),
    H - padB - ((v - vLo) / (vHi - vLo || 1)) * (H - padT - padB),
  ];
  const hpLine = curve.map(p => toXY(p.rpm, p.hp).join(',')).join(' ');
  const tqLine = curve.map(p => toXY(p.rpm, p.tq).join(',')).join(' ');
  const baseY = H - padB;
  return `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">
    <line x1="${padL}" y1="${padT}" x2="${padL}" y2="${baseY}" stroke="var(--gray-line)" stroke-width="1"/>
    <line x1="${padL}" y1="${baseY}" x2="${W - padR}" y2="${baseY}" stroke="var(--gray-line)" stroke-width="1"/>
    <text x="${padL}" y="${H - 6}" font-size="10" fill="var(--muted)">${rpmLo.toLocaleString()}</text>
    <text x="${W - padR}" y="${H - 6}" font-size="10" fill="var(--muted)" text-anchor="end">${rpmHi.toLocaleString()} rpm</text>
    <polyline points="${hpLine}" fill="none" stroke="var(--black)" stroke-width="1.8"/>
    <polyline points="${tqLine}" fill="none" stroke="var(--red)" stroke-width="1.8"/>
  </svg>`;
}

function renderPowerEstimate(metrics) {
  const summaryEl = document.getElementById('powerSummary');
  const chartEl = document.getElementById('powerChart');
  const noteEl = document.getElementById('fuelTypeNote');
  if (!metrics) { summaryEl.innerHTML = ''; chartEl.innerHTML = ''; noteEl.innerHTML = ''; return; }

  const pc = metrics.powerCurve;
  if (!pc || !pc.available) {
    summaryEl.innerHTML = `<div class="power-summary-line">Fuel flow channel not available for this log, power estimate not evaluated.</div>`;
    chartEl.innerHTML = '';
  } else {
    summaryEl.innerHTML = `<div class="power-summary-line">
      <b>Peak ${pc.peakHp.hp.toFixed(0)} hp</b> at ${pc.peakHp.rpm.toLocaleString()} rpm &nbsp;&middot;&nbsp;
      <b>Peak ${pc.peakTq.tq.toFixed(0)} lb-ft</b> at ${pc.peakTq.rpm.toLocaleString()} rpm
    </div>`;
    chartEl.innerHTML = `
      <div class="power-legend">
        <span><span class="swatch" style="background:var(--black)"></span>Horsepower</span>
        <span><span class="swatch" style="background:var(--red)"></span>Torque (lb-ft)</span>
      </div>
      ${powerCurveSVG(pc.curve)}
      <div class="power-caveat">Estimated from fuel flow (BSFC ${pc.bsfc.toFixed(2)} lb/hp/hr assumed for this fuel), not a dyno measurement. Treat as a trend across the pull, not an exact number, actual BSFC varies with engine efficiency and tune quality.</div>
    `;
  }

  const ft = metrics.fuelType;
  if (ft && ft.available) {
    noteEl.innerHTML = `<div class="fuel-type-note"><b>Fuel type guess:</b> ${ft.guess} (confidence: ${ft.confidence}). ${ft.note}</div>`;
  } else {
    noteEl.innerHTML = '';
  }
}

function renderResults(result, fullRows) {
  processing.classList.remove('active');
  const { flags, window, channels, segmentation, metrics } = result;
  const nRows = window ? (window.end - window.start + 1) : fullRows;

  const critical = flags.filter(f => f.sev === 'critical');
  const warn = flags.filter(f => f.sev === 'warn');
  const rest = flags.filter(f => f.sev !== 'critical' && f.sev !== 'warn');

  summaryLine.innerHTML = `
    <span class="count-critical"><b>${critical.length}</b> critical</span>
    <span class="count-warn"><b>${warn.length}</b> warning${warn.length===1?'':'s'}</span>
    <span class="count-clear"><b>${flags.filter(f=>f.sev==='ok').length}</b> clear</span>
    <span style="color:var(--ink-muted)">- ${nRows.toLocaleString()} of ${fullRows.toLocaleString()} rows</span>
  `;
  const windowNote = document.getElementById('windowNote');
  if (window) {
    const secs = (nRows / window.rateHz).toFixed(1);
    windowNote.textContent = `Focused on the wide-open-throttle pull plus 5 seconds on each side (~${secs}s window` +
      (window.exact ? ', exact timing from the log' : ', timing estimated at ~20 samples/sec') + `).`;
  } else {
    windowNote.textContent = '';
  }

  const segmentTimeline = document.getElementById('segmentTimeline');
  const segmentConfidence = document.getElementById('segmentConfidence');
  if (segmentation && window) {
    segmentTimeline.innerHTML = segmentation.segments.map(s => {
      const secs = ((s.endIdx - s.startIdx + 1) / window.rateHz).toFixed(1);
      return `<div class="seg"><b>${s.phase}</b>${secs}s</div>`;
    }).join('');
    const shiftNote = segmentation.shifts.length
      ? `${segmentation.shifts.length} shift${segmentation.shifts.length===1?'':''} detected.`
      : `No shifts detected (single gear, or the pull was too short to catch one).`;
    segmentConfidence.textContent = `Phase confidence: ${segmentation.confidence} (launch marker: ${segmentation.runStart.source}). ${shiftNote}`;
  } else {
    segmentTimeline.innerHTML = '';
    segmentConfidence.textContent = '';
  }
  renderShiftAnalysis(segmentation, window);
  renderPowerEstimate(metrics);

  checkList.innerHTML = '';
  const clearOnes = [];
  for (const f of [...critical, ...warn, ...rest]) {
    if (f.sev === 'ok') { clearOnes.push(f); continue; }
    const row = document.createElement('div');
    row.className = 'check-row';
    const spark = f.idxs.length || f.channel ? sparklineSVG(channels[f.channel], f.idxs, f.sev) : '';
    row.innerHTML = `
      <div class="head">
        <div class="name">${f.name}</div>
        <div class="sev-tag sev-${f.sev}">${f.sev}</div>
      </div>
      <div class="msg">${f.msg}</div>
      ${spark ? `<div class="sparkline">${spark}</div>` : ''}
    `;
    checkList.appendChild(row);
  }

  if (clearOnes.length) {
    clearList.style.display = 'block';
    clearItems.innerHTML = clearOnes.map(f => `<div class="clear-item">${f.name}  -  ${f.msg}</div>`).join('');
  } else {
    clearList.style.display = 'none';
  }

  results.classList.add('active');
}

function timePoints(arr, runStartIdx, rateHz) {
  const n = arr.length;
  const maxPts = 200;
  const step = Math.max(1, Math.floor(n / maxPts));
  const pts = [];
  for (let i = 0; i < n; i += step) if (!Number.isNaN(arr[i])) pts.push([(i - runStartIdx) / rateHz, arr[i]]);
  return pts;
}

function overlaySVG(seriesA, seriesB, runStartA, runStartB, rateHzA, rateHzB) {
  const ptsA = timePoints(seriesA, runStartA, rateHzA);
  const ptsB = timePoints(seriesB, runStartB, rateHzB);
  const allT = [...ptsA, ...ptsB].map(p => p[0]);
  const allV = [...ptsA, ...ptsB].map(p => p[1]);
  const tLo = Math.min(...allT), tHi = Math.max(...allT);
  const vLo = Math.min(...allV), vHi = Math.max(...allV);
  const tRange = (tHi - tLo) || 1;
  const vRange = (vHi - vLo) || 1;
  const W = 600, H = 100, pad = 6;
  const toXY = ([t, v]) => [((t - tLo) / tRange) * W, H - pad - ((v - vLo) / vRange) * (H - 2 * pad)];
  const lineA = ptsA.map(toXY).map(p => p.join(',')).join(' ');
  const lineB = ptsB.map(toXY).map(p => p.join(',')).join(' ');
  const zeroX = ((0 - tLo) / tRange) * W;
  return `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="none">
    <line x1="${zeroX.toFixed(1)}" y1="0" x2="${zeroX.toFixed(1)}" y2="${H}" stroke="var(--red)" stroke-width="1" stroke-dasharray="3,2"/>
    <polyline points="${lineA}" fill="none" stroke="var(--black)" stroke-width="1.6"/>
    <polyline points="${lineB}" fill="none" stroke="var(--cmp-b)" stroke-width="1.6"/>
  </svg>`;
}

function fmtSpool(spool) {
  if (!spool) return ' - ';
  return spool.reached ? `${spool.seconds.toFixed(2)}s` : 'never reached';
}
function fmtFade(fade) {
  if (!fade) return ' - ';
  return `${fade.fadePsi >= 0 ? '+' : ''}${fade.fadePsi.toFixed(1)} psi`;
}
function fmtAfrBand(rows) {
  if (!rows || !rows.length) return 'even';
  const leanest = rows.reduce((a, b) => (b.medianErr > a.medianErr ? b : a));
  return leanest.medianErr > 0.3 ? `${leanest.band}-${leanest.band+500} rpm, ${leanest.medianErr.toFixed(2)} lean` : 'even';
}
function fmtDuty(duty) {
  if (!duty) return ' - ';
  return `${duty.max.toFixed(0)}%`;
}

function fmtClComp(cl) {
  if (!cl || !cl.available) return 'unavailable';
  if (!cl.activeLikely) return 'flat (inactive?)';
  return `${cl.medianSigned>=0?'+':''}${cl.medianSigned.toFixed(1)}% median, ${cl.p95.toFixed(0)}% peak`;
}
function fmtShiftSummary(seg) {
  if (!seg || !seg.shifts.length) return 'none detected';
  const avg = seg.shifts.reduce((a,s)=>a+s.rpmBefore,0) / seg.shifts.length;
  return `${seg.shifts.length} shift${seg.shifts.length===1?'':'s'}, avg ${avg.toFixed(0)} rpm`;
}

function renderComparison(logA, logB) {
  results.classList.remove('active');
  compareZone.style.display = 'none';
  const A = logA.result, B = logB.result;
  const peakA = nanmax(A.channels.boost_psig), peakB = nanmax(B.channels.boost_psig);
  const critA = A.flags.filter(f => f.sev === 'critical').length, critB = B.flags.filter(f => f.sev === 'critical').length;
  const warnA = A.flags.filter(f => f.sev === 'warn').length, warnB = B.flags.filter(f => f.sev === 'warn').length;

  const rows = [
    ['Peak Boost', `${peakA.toFixed(1)} psi`, `${peakB.toFixed(1)} psi`],
    ['Spool Time', fmtSpool(A.metrics.spool), fmtSpool(B.metrics.spool)],
    ['Boost Fade', fmtFade(A.metrics.fade), fmtFade(B.metrics.fade)],
    ['Leanest Band', fmtAfrBand(A.metrics.afrByBand), fmtAfrBand(B.metrics.afrByBand)],
    ['Peak Duty Cycle', fmtDuty(A.metrics.duty), fmtDuty(B.metrics.duty)],
    ['CL Comp', fmtClComp(A.metrics.clComp), fmtClComp(B.metrics.clComp)],
    ['Shifts', fmtShiftSummary(A.segmentation), fmtShiftSummary(B.segmentation)],
    ['Critical Flags', String(critA), String(critB)],
    ['Warnings', String(warnA), String(warnB)],
  ];

  compareResults.innerHTML = `
    <div class="cmp-header">
      <div class="col label"></div>
      <div class="col">${logA.name}</div>
      <div class="col">${logB.name}</div>
    </div>
    ${rows.map(([label, a, b]) => `
      <div class="cmp-row">
        <div class="col label">${label}</div>
        <div class="col">${a}</div>
        <div class="col">${b}</div>
      </div>
    `).join('')}
    <div class="cmp-overlay">
      <div class="legend">
        <span><span class="swatch" style="background:var(--ink)"></span>${logA.name}</span>
        <span><span class="swatch" style="background:var(--cmp-b)"></span>${logB.name}</span>
      </div>
      ${overlaySVG(A.channels.boost_psig, B.channels.boost_psig, A.metrics.runStart.idx, B.metrics.runStart.idx, A.window.rateHz, B.window.rateHz)}
      <div style="font-size:11px;color:var(--ink-muted);margin-top:4px;">
        Boost trace, aligned to the actual run start (red dashed line), ${logA.name}: ${A.metrics.runStart.source}, ${logB.name}: ${B.metrics.runStart.source}.
      </div>
    </div>
    <a class="reset-link" id="compareResetLink">start over</a>
  `;
  document.getElementById('compareResetLink').addEventListener('click', () => location.reload());
}